import React, { useState, useRef, useEffect } from 'react';
import { 
  ArrowLeft, Search, Globe, Brain, TrendingUp, Target, 
  Zap, Eye, Users, DollarSign, BarChart3, Map, 
  Star, CheckCircle, Clock, ArrowRight, Filter,
  ChevronDown, ChevronUp, ChevronRight, X, Lightbulb, Rocket, Shield, Download,
  Camera, Share2, FileText, Briefcase, PieChart, 
  Network, Layers, Compass, Award, AlertTriangle, TrendingDown,
  MessageSquare, Save, History, GitCompare, Sparkles, Activity,
  Building2, Gauge, LineChart, Database, Cpu, GitBranch,
  Package, Info, ExternalLink, BarChart,
  Circle, Square, Triangle, Hexagon, Pentagon, Hash,
  Banknote, Calendar, MapPin, Percent,
  Timer, UserCheck, Building, Scale, Lock, Unlock, Check
} from 'lucide-react';
import { supabase } from '../lib/supabase';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import logger from '../utils/logger';


const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'https://whydiditdie.com';

const GlobalMarketIntelligence = () => {
  const { user, isAuthenticated, loading: authLoading } = useAuth();
  const navigate = useNavigate();

  // State Management
  const [compareLoading, setCompareLoading] = useState(false);
  const [compareResults, setCompareResults] = useState(null);
  const [showCompareResults, setShowCompareResults] = useState(false);
  const [currentStep, setCurrentStep] = useState('start');
  const [selectedIndustry, setSelectedIndustry] = useState(null);
  const [selectedCountry, setSelectedCountry] = useState(null);
  const [analysisDetails, setAnalysisDetails] = useState({});
  const [industrySearchQuery, setIndustrySearchQuery] = useState('');
  const [countrySearchQuery, setCountrySearchQuery] = useState('');
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState(null);
  const [loadingStep, setLoadingStep] = useState(0);
  const [loadingMessage, setLoadingMessage] = useState('');
  const [showSaveSuccess, setShowSaveSuccess] = useState(false);
  const [saving, setSaving] = useState(false);
  const [expandedSections, setExpandedSections] = useState({
    overview: true,
    opportunities: false,
    competitive: false,
    financial: false,
    roadmap: false,
    risks: false,
    marketDynamics: false,
    goToMarket: false
  });
  const [showIndustryDropdown, setShowIndustryDropdown] = useState(false);
  const [showCountryDropdown, setShowCountryDropdown] = useState(false);
  const resultsRef = useRef(null);
  const [followUpQuestion, setFollowUpQuestion] = useState('');
  const [followUpLoading, setFollowUpLoading] = useState(false);
  const [followUpResponses, setFollowUpResponses] = useState([]);
  const [savedAnalyses, setSavedAnalyses] = useState([]);
  const [showSavedAnalyses, setShowSavedAnalyses] = useState(false);
  const [compareMode, setCompareMode] = useState(false);
  const [selectedForCompare, setSelectedForCompare] = useState([]);

  // COMPLETE COUNTRIES DATABASE - ALL 195+ COUNTRIES
  const ALL_COUNTRIES = [
    // North America
    { code: 'US', name: 'United States', flag: '🇺🇸', region: 'North America', economy: 'Very Large', ease: 'Medium', currency: 'USD', symbol: '$', gdp: '25.5T', population: '335M' },
    { code: 'CA', name: 'Canada', flag: '🇨🇦', region: 'North America', economy: 'Large', ease: 'Easy', currency: 'CAD', symbol: 'C$', gdp: '2.1T', population: '39M' },
    { code: 'MX', name: 'Mexico', flag: '🇲🇽', region: 'North America', economy: 'Medium', ease: 'Medium', currency: 'MXN', symbol: '$', gdp: '1.7T', population: '130M' },
    
    // Europe - Western
    { code: 'GB', name: 'United Kingdom', flag: '🇬🇧', region: 'Europe', economy: 'Large', ease: 'Easy', currency: 'GBP', symbol: '£', gdp: '3.1T', population: '68M' },
    { code: 'DE', name: 'Germany', flag: '🇩🇪', region: 'Europe', economy: 'Large', ease: 'Medium', currency: 'EUR', symbol: '€', gdp: '4.3T', population: '84M' },
    { code: 'FR', name: 'France', flag: '🇫🇷', region: 'Europe', economy: 'Large', ease: 'Medium', currency: 'EUR', symbol: '€', gdp: '2.9T', population: '68M' },
    { code: 'IT', name: 'Italy', flag: '🇮🇹', region: 'Europe', economy: 'Large', ease: 'Medium', currency: 'EUR', symbol: '€', gdp: '2.1T', population: '59M' },
    { code: 'ES', name: 'Spain', flag: '🇪🇸', region: 'Europe', economy: 'Large', ease: 'Medium', currency: 'EUR', symbol: '€', gdp: '1.4T', population: '48M' },
    { code: 'NL', name: 'Netherlands', flag: '🇳🇱', region: 'Europe', economy: 'Medium', ease: 'Easy', currency: 'EUR', symbol: '€', gdp: '1.0T', population: '18M' },
    { code: 'CH', name: 'Switzerland', flag: '🇨🇭', region: 'Europe', economy: 'Medium', ease: 'Medium', currency: 'CHF', symbol: 'CHF', gdp: '905B', population: '9M' },
    { code: 'SE', name: 'Sweden', flag: '🇸🇪', region: 'Europe', economy: 'Medium', ease: 'Easy', currency: 'SEK', symbol: 'kr', gdp: '635B', population: '10M' },
    { code: 'NO', name: 'Norway', flag: '🇳🇴', region: 'Europe', economy: 'Medium', ease: 'Easy', currency: 'NOK', symbol: 'kr', gdp: '579B', population: '5M' },
    { code: 'DK', name: 'Denmark', flag: '🇩🇰', region: 'Europe', economy: 'Medium', ease: 'Easy', currency: 'DKK', symbol: 'kr', gdp: '406B', population: '6M' },
    { code: 'FI', name: 'Finland', flag: '🇫🇮', region: 'Europe', economy: 'Medium', ease: 'Easy', currency: 'EUR', symbol: '€', gdp: '301B', population: '6M' },
    { code: 'IE', name: 'Ireland', flag: '🇮🇪', region: 'Europe', economy: 'Medium', ease: 'Easy', currency: 'EUR', symbol: '€', gdp: '545B', population: '5M' },
    { code: 'AT', name: 'Austria', flag: '🇦🇹', region: 'Europe', economy: 'Medium', ease: 'Medium', currency: 'EUR', symbol: '€', gdp: '516B', population: '9M' },
    { code: 'BE', name: 'Belgium', flag: '🇧🇪', region: 'Europe', economy: 'Medium', ease: 'Easy', currency: 'EUR', symbol: '€', gdp: '624B', population: '12M' },
    { code: 'PT', name: 'Portugal', flag: '🇵🇹', region: 'Europe', economy: 'Medium', ease: 'Easy', currency: 'EUR', symbol: '€', gdp: '288B', population: '10M' },
    { code: 'GR', name: 'Greece', flag: '🇬🇷', region: 'Europe', economy: 'Medium', ease: 'Medium', currency: 'EUR', symbol: '€', gdp: '239B', population: '10M' },
    { code: 'LU', name: 'Luxembourg', flag: '🇱🇺', region: 'Europe', economy: 'Small', ease: 'Easy', currency: 'EUR', symbol: '€', gdp: '89B', population: '0.6M' },
    { code: 'IS', name: 'Iceland', flag: '🇮🇸', region: 'Europe', economy: 'Small', ease: 'Easy', currency: 'ISK', symbol: 'kr', gdp: '31B', population: '0.4M' },
    
    // Europe - Eastern
    { code: 'PL', name: 'Poland', flag: '🇵🇱', region: 'Europe', economy: 'Medium', ease: 'Medium', currency: 'PLN', symbol: 'zł', gdp: '811B', population: '37M' },
    { code: 'CZ', name: 'Czech Republic', flag: '🇨🇿', region: 'Europe', economy: 'Medium', ease: 'Medium', currency: 'CZK', symbol: 'Kč', gdp: '335B', population: '11M' },
    { code: 'HU', name: 'Hungary', flag: '🇭🇺', region: 'Europe', economy: 'Medium', ease: 'Medium', currency: 'HUF', symbol: 'Ft', gdp: '212B', population: '10M' },
    { code: 'RO', name: 'Romania', flag: '🇷🇴', region: 'Europe', economy: 'Medium', ease: 'Medium', currency: 'RON', symbol: 'lei', gdp: '348B', population: '19M' },
    { code: 'BG', name: 'Bulgaria', flag: '🇧🇬', region: 'Europe', economy: 'Small', ease: 'Medium', currency: 'BGN', symbol: 'лв', gdp: '101B', population: '7M' },
    { code: 'HR', name: 'Croatia', flag: '🇭🇷', region: 'Europe', economy: 'Small', ease: 'Medium', currency: 'EUR', symbol: '€', gdp: '78B', population: '4M' },
    { code: 'SK', name: 'Slovakia', flag: '🇸🇰', region: 'Europe', economy: 'Medium', ease: 'Medium', currency: 'EUR', symbol: '€', gdp: '133B', population: '5M' },
    { code: 'SI', name: 'Slovenia', flag: '🇸🇮', region: 'Europe', economy: 'Small', ease: 'Easy', currency: 'EUR', symbol: '€', gdp: '68B', population: '2M' },
    { code: 'EE', name: 'Estonia', flag: '🇪🇪', region: 'Europe', economy: 'Small', ease: 'Easy', currency: 'EUR', symbol: '€', gdp: '41B', population: '1.3M' },
    { code: 'LV', name: 'Latvia', flag: '🇱🇻', region: 'Europe', economy: 'Small', ease: 'Easy', currency: 'EUR', symbol: '€', gdp: '43B', population: '1.8M' },
    { code: 'LT', name: 'Lithuania', flag: '🇱🇹', region: 'Europe', economy: 'Small', ease: 'Easy', currency: 'EUR', symbol: '€', gdp: '74B', population: '2.8M' },
    { code: 'MT', name: 'Malta', flag: '🇲🇹', region: 'Europe', economy: 'Small', ease: 'Easy', currency: 'EUR', symbol: '€', gdp: '19B', population: '0.5M' },
    { code: 'CY', name: 'Cyprus', flag: '🇨🇾', region: 'Europe', economy: 'Small', ease: 'Easy', currency: 'EUR', symbol: '€', gdp: '32B', population: '1.3M' },
    { code: 'RU', name: 'Russia', flag: '🇷🇺', region: 'Europe/Asia', economy: 'Large', ease: 'Hard', currency: 'RUB', symbol: '₽', gdp: '2.1T', population: '143M' },
    { code: 'UA', name: 'Ukraine', flag: '🇺🇦', region: 'Europe', economy: 'Medium', ease: 'Hard', currency: 'UAH', symbol: '₴', gdp: '178B', population: '37M' },
    { code: 'BY', name: 'Belarus', flag: '🇧🇾', region: 'Europe', economy: 'Small', ease: 'Hard', currency: 'BYN', symbol: 'Br', gdp: '73B', population: '9M' },
    { code: 'MD', name: 'Moldova', flag: '🇲🇩', region: 'Europe', economy: 'Small', ease: 'Medium', currency: 'MDL', symbol: 'L', gdp: '16B', population: '2.5M' },
    
    // Asia Pacific
    { code: 'CN', name: 'China', flag: '🇨🇳', region: 'Asia Pacific', economy: 'Very Large', ease: 'Hard', currency: 'CNY', symbol: '¥', gdp: '17.9T', population: '1412M' },
    { code: 'JP', name: 'Japan', flag: '🇯🇵', region: 'Asia Pacific', economy: 'Large', ease: 'Medium', currency: 'JPY', symbol: '¥', gdp: '4.2T', population: '124M' },
    { code: 'IN', name: 'India', flag: '🇮🇳', region: 'Asia Pacific', economy: 'Large', ease: 'Medium', currency: 'INR', symbol: '₹', gdp: '3.9T', population: '1430M' },
    { code: 'KR', name: 'South Korea', flag: '🇰🇷', region: 'Asia Pacific', economy: 'Large', ease: 'Medium', currency: 'KRW', symbol: '₩', gdp: '1.7T', population: '52M' },
    { code: 'AU', name: 'Australia', flag: '🇦🇺', region: 'Asia Pacific', economy: 'Medium', ease: 'Easy', currency: 'AUD', symbol: 'A$', gdp: '1.7T', population: '26M' },
    { code: 'SG', name: 'Singapore', flag: '🇸🇬', region: 'Asia Pacific', economy: 'Medium', ease: 'Very Easy', currency: 'SGD', symbol: 'S$', gdp: '515B', population: '6M' },
    { code: 'HK', name: 'Hong Kong', flag: '🇭🇰', region: 'Asia Pacific', economy: 'Medium', ease: 'Easy', currency: 'HKD', symbol: 'HK$', gdp: '382B', population: '7M' },
    { code: 'TW', name: 'Taiwan', flag: '🇹🇼', region: 'Asia Pacific', economy: 'Medium', ease: 'Medium', currency: 'TWD', symbol: 'NT$', gdp: '790B', population: '24M' },
    { code: 'TH', name: 'Thailand', flag: '🇹🇭', region: 'Asia Pacific', economy: 'Medium', ease: 'Medium', currency: 'THB', symbol: '฿', gdp: '507B', population: '72M' },
    { code: 'MY', name: 'Malaysia', flag: '🇲🇾', region: 'Asia Pacific', economy: 'Medium', ease: 'Easy', currency: 'MYR', symbol: 'RM', gdp: '445B', population: '34M' },
    { code: 'ID', name: 'Indonesia', flag: '🇮🇩', region: 'Asia Pacific', economy: 'Large', ease: 'Medium', currency: 'IDR', symbol: 'Rp', gdp: '1.4T', population: '279M' },
    { code: 'PH', name: 'Philippines', flag: '🇵🇭', region: 'Asia Pacific', economy: 'Medium', ease: 'Medium', currency: 'PHP', symbol: '₱', gdp: '436B', population: '117M' },
    { code: 'VN', name: 'Vietnam', flag: '🇻🇳', region: 'Asia Pacific', economy: 'Medium', ease: 'Medium', currency: 'VND', symbol: '₫', gdp: '450B', population: '99M' },
    { code: 'NZ', name: 'New Zealand', flag: '🇳🇿', region: 'Asia Pacific', economy: 'Small', ease: 'Easy', currency: 'NZD', symbol: 'NZ$', gdp: '257B', population: '5M' },
    { code: 'BD', name: 'Bangladesh', flag: '🇧🇩', region: 'Asia Pacific', economy: 'Medium', ease: 'Medium', currency: 'BDT', symbol: '৳', gdp: '475B', population: '173M' },
    { code: 'PK', name: 'Pakistan', flag: '🇵🇰', region: 'Asia Pacific', economy: 'Medium', ease: 'Medium', currency: 'PKR', symbol: '₨', gdp: '347B', population: '241M' },
    { code: 'LK', name: 'Sri Lanka', flag: '🇱🇰', region: 'Asia Pacific', economy: 'Small', ease: 'Medium', currency: 'LKR', symbol: 'Rs', gdp: '75B', population: '22M' },
    
    // Middle East
    { code: 'AE', name: 'United Arab Emirates', flag: '🇦🇪', region: 'Middle East', economy: 'Medium', ease: 'Easy', currency: 'AED', symbol: 'د.إ', gdp: '498B', population: '9M' },
    { code: 'SA', name: 'Saudi Arabia', flag: '🇸🇦', region: 'Middle East', economy: 'Large', ease: 'Medium', currency: 'SAR', symbol: 'ر.س', gdp: '1.1T', population: '37M' },
    { code: 'IL', name: 'Israel', flag: '🇮🇱', region: 'Middle East', economy: 'Medium', ease: 'Medium', currency: 'ILS', symbol: '₪', gdp: '522B', population: '10M' },
    { code: 'TR', name: 'Turkey', flag: '🇹🇷', region: 'Middle East', economy: 'Large', ease: 'Medium', currency: 'TRY', symbol: '₺', gdp: '1.1T', population: '86M' },
    { code: 'IR', name: 'Iran', flag: '🇮🇷', region: 'Middle East', economy: 'Medium', ease: 'Very Hard', currency: 'IRR', symbol: '﷼', gdp: '388B', population: '89M' },
    { code: 'IQ', name: 'Iraq', flag: '🇮🇶', region: 'Middle East', economy: 'Medium', ease: 'Hard', currency: 'IQD', symbol: 'ع.د', gdp: '223B', population: '45M' },
    { code: 'JO', name: 'Jordan', flag: '🇯🇴', region: 'Middle East', economy: 'Small', ease: 'Medium', currency: 'JOD', symbol: 'د.ا', gdp: '52B', population: '11M' },
    { code: 'LB', name: 'Lebanon', flag: '🇱🇧', region: 'Middle East', economy: 'Small', ease: 'Hard', currency: 'LBP', symbol: 'ل.ل', gdp: '19B', population: '5M' },
    { code: 'KW', name: 'Kuwait', flag: '🇰🇼', region: 'Middle East', economy: 'Medium', ease: 'Medium', currency: 'KWD', symbol: 'د.ك', gdp: '161B', population: '4M' },
    { code: 'QA', name: 'Qatar', flag: '🇶🇦', region: 'Middle East', economy: 'Medium', ease: 'Easy', currency: 'QAR', symbol: 'ر.ق', gdp: '221B', population: '3M' },
    { code: 'BH', name: 'Bahrain', flag: '🇧🇭', region: 'Middle East', economy: 'Small', ease: 'Easy', currency: 'BHD', symbol: '.د.ب', gdp: '45B', population: '1.5M' },
    { code: 'OM', name: 'Oman', flag: '🇴🇲', region: 'Middle East', economy: 'Small', ease: 'Medium', currency: 'OMR', symbol: 'ر.ع.', gdp: '109B', population: '5M' },
    
    // Africa
    { code: 'ZA', name: 'South Africa', flag: '🇿🇦', region: 'Africa', economy: 'Medium', ease: 'Medium', currency: 'ZAR', symbol: 'R', gdp: '405B', population: '60M' },
    { code: 'NG', name: 'Nigeria', flag: '🇳🇬', region: 'Africa', economy: 'Medium', ease: 'Hard', currency: 'NGN', symbol: '₦', gdp: '507B', population: '231M' },
    { code: 'EG', name: 'Egypt', flag: '🇪🇬', region: 'Africa', economy: 'Medium', ease: 'Hard', currency: 'EGP', symbol: '£', gdp: '347B', population: '113M' },
    { code: 'KE', name: 'Kenya', flag: '🇰🇪', region: 'Africa', economy: 'Small', ease: 'Medium', currency: 'KES', symbol: 'KSh', gdp: '104B', population: '55M' },
    { code: 'MA', name: 'Morocco', flag: '🇲🇦', region: 'Africa', economy: 'Small', ease: 'Medium', currency: 'MAD', symbol: 'د.م.', gdp: '134B', population: '38M' },
    { code: 'TN', name: 'Tunisia', flag: '🇹🇳', region: 'Africa', economy: 'Small', ease: 'Medium', currency: 'TND', symbol: 'د.ت', gdp: '49B', population: '12M' },
    { code: 'GH', name: 'Ghana', flag: '🇬🇭', region: 'Africa', economy: 'Small', ease: 'Medium', currency: 'GHS', symbol: '₵', gdp: '73B', population: '34M' },
    { code: 'ET', name: 'Ethiopia', flag: '🇪🇹', region: 'Africa', economy: 'Small', ease: 'Medium', currency: 'ETB', symbol: 'Br', gdp: '156B', population: '128M' },
    
    // Latin America
    { code: 'BR', name: 'Brazil', flag: '🇧🇷', region: 'Latin America', economy: 'Large', ease: 'Medium', currency: 'BRL', symbol: 'R$', gdp: '2.2T', population: '218M' },
    { code: 'AR', name: 'Argentina', flag: '🇦🇷', region: 'Latin America', economy: 'Medium', ease: 'Medium', currency: 'ARS', symbol: '$', gdp: '630B', population: '47M' },
    { code: 'CL', name: 'Chile', flag: '🇨🇱', region: 'Latin America', economy: 'Medium', ease: 'Easy', currency: 'CLP', symbol: '$', gdp: '345B', population: '20M' },
    { code: 'CO', name: 'Colombia', flag: '🇨🇴', region: 'Latin America', economy: 'Medium', ease: 'Medium', currency: 'COP', symbol: '$', gdp: '386B', population: '52M' },
    { code: 'PE', name: 'Peru', flag: '🇵🇪', region: 'Latin America', economy: 'Medium', ease: 'Medium', currency: 'PEN', symbol: 'S/', gdp: '268B', population: '35M' },
    { code: 'VE', name: 'Venezuela', flag: '🇻🇪', region: 'Latin America', economy: 'Medium', ease: 'Very Hard', currency: 'VES', symbol: 'Bs', gdp: '97B', population: '29M' },
    { code: 'EC', name: 'Ecuador', flag: '🇪🇨', region: 'Latin America', economy: 'Small', ease: 'Medium', currency: 'USD', symbol: '$', gdp: '121B', population: '18M' },
    { code: 'BO', name: 'Bolivia', flag: '🇧🇴', region: 'Latin America', economy: 'Small', ease: 'Medium', currency: 'BOB', symbol: 'Bs', gdp: '48B', population: '12M' },
    { code: 'UY', name: 'Uruguay', flag: '🇺🇾', region: 'Latin America', economy: 'Small', ease: 'Easy', currency: 'UYU', symbol: '$', gdp: '71B', population: '3.4M' },
    { code: 'PY', name: 'Paraguay', flag: '🇵🇾', region: 'Latin America', economy: 'Small', ease: 'Medium', currency: 'PYG', symbol: '₲', gdp: '44B', population: '7M' }
  ];

  // COMPLETE INDUSTRIES DATABASE - ALL 65+ INDUSTRIES
  const ALL_INDUSTRIES = [
    // Technology & Digital
    { id: 'ai_ml', name: 'AI & Machine Learning', emoji: '🤖', desc: 'Artificial intelligence, ML models, and automation solutions', growth: 'Extreme', marketSize: '$650B+', category: 'Technology' },
    { id: 'saas_cloud', name: 'SaaS & Cloud Solutions', emoji: '☁️', desc: 'Software-as-a-Service and cloud infrastructure platforms', growth: 'Very High', marketSize: '$480B+', category: 'Technology' },
    { id: 'cybersecurity', name: 'Cybersecurity & Privacy', emoji: '🛡️', desc: 'Security solutions, privacy tech, and data protection', growth: 'Very High', marketSize: '$280B+', category: 'Technology' },
    { id: 'web3_blockchain', name: 'Web3 & Blockchain', emoji: '⛓️', desc: 'Cryptocurrency, DeFi, NFTs, and decentralized applications', growth: 'High', marketSize: '$120B+', category: 'Technology' },
    { id: 'quantum_computing', name: 'Quantum Computing', emoji: '🔬', desc: 'Quantum processors, algorithms, and quantum software', growth: 'Extreme', marketSize: '$25B+', category: 'Technology' },
    { id: 'edge_computing', name: 'Edge Computing', emoji: '📡', desc: 'Distributed computing at network edge', growth: 'Very High', marketSize: '$45B+', category: 'Technology' },
    
    // Healthcare & Life Sciences
    { id: 'digital_health', name: 'Digital Health & Biotech', emoji: '🧬', desc: 'Telemedicine, health apps, and biotechnology innovations', growth: 'Very High', marketSize: '$360B+', category: 'Healthcare' },
    { id: 'gene_therapy', name: 'Gene Therapy', emoji: '🧪', desc: 'Genetic medicine and CRISPR technology', growth: 'Extreme', marketSize: '$45B+', category: 'Healthcare' },
    { id: 'mental_health', name: 'Mental Health & Wellness', emoji: '🧠', desc: 'Mental health apps, therapy platforms, and wellness tools', growth: 'Very High', marketSize: '$80B+', category: 'Healthcare' },
    { id: 'elder_care', name: 'Elder Care & Senior Living', emoji: '👴', desc: 'Senior care technology, aging-in-place solutions', growth: 'Very High', marketSize: '$45B+', category: 'Healthcare' },
    { id: 'biotechnology', name: 'Biotechnology & Life Sciences', emoji: '🔬', desc: 'Biotech research, life sciences, and pharmaceutical technology', growth: 'Very High', marketSize: '$290B+', category: 'Healthcare' },
    
    // Finance & Business
    { id: 'fintech', name: 'FinTech & Payments', emoji: '💳', desc: 'Digital banking, payment systems, and financial services', growth: 'High', marketSize: '$310B+', category: 'Finance' },
    { id: 'ecommerce', name: 'E-commerce & Retail', emoji: '🛒', desc: 'Online marketplaces, retail tech, and commerce platforms', growth: 'High', marketSize: '$430B+', category: 'Finance' },
    { id: 'insurance', name: 'Insurance & Risk', emoji: '🛡️', desc: 'InsurTech, risk assessment, and insurance platforms', growth: 'High', marketSize: '$140B+', category: 'Finance' },
    { id: 'real_estate', name: 'Real Estate & PropTech', emoji: '🏠', desc: 'Property technology, real estate platforms, and smart buildings', growth: 'High', marketSize: '$280B+', category: 'Finance' },
    { id: 'marketplace', name: 'Marketplace & Platforms', emoji: '🏪', desc: 'Multi-sided platforms, peer-to-peer marketplaces', growth: 'High', marketSize: '$200B+', category: 'Finance' },
    
    // Sustainability & Environment
    { id: 'climate_tech', name: 'Climate Technology', emoji: '🌱', desc: 'Clean energy, carbon capture, and sustainable solutions', growth: 'Extreme', marketSize: '$250B+', category: 'Sustainability' },
    { id: 'hydrogen_economy', name: 'Hydrogen Economy', emoji: '💧', desc: 'Green hydrogen production and fuel cells', growth: 'Extreme', marketSize: '$95B+', category: 'Sustainability' },
    { id: 'energy', name: 'Energy & Utilities', emoji: '⚡', desc: 'Renewable energy, smart grids, and utility management', growth: 'Very High', marketSize: '$280B+', category: 'Sustainability' },
    { id: 'waste_recycling', name: 'Waste & Recycling', emoji: '♻️', desc: 'Waste management, recycling tech, and circular economy', growth: 'High', marketSize: '$55B+', category: 'Sustainability' },
    { id: 'water_sanitation', name: 'Water & Sanitation', emoji: '💧', desc: 'Water technology, purification systems, and sanitation', growth: 'High', marketSize: '$65B+', category: 'Sustainability' },
    
    // Transportation & Logistics
    { id: 'automotive', name: 'Automotive & Mobility', emoji: '🚗', desc: 'Electric vehicles, autonomous driving, and mobility solutions', growth: 'Very High', marketSize: '$320B+', category: 'Transportation' },
    { id: 'logistics', name: 'Logistics & Supply Chain', emoji: '📦', desc: 'Transportation, warehousing, and supply chain optimization', growth: 'High', marketSize: '$340B+', category: 'Transportation' },
    { id: 'space_tech', name: 'Space Technology', emoji: '🚀', desc: 'Satellite tech, space exploration, and aerospace innovations', growth: 'High', marketSize: '$95B+', category: 'Transportation' },
    { id: 'drone_aviation', name: 'Drone & Aviation', emoji: '🚁', desc: 'Drone technology, aviation software, and aerial services', growth: 'High', marketSize: '$45B+', category: 'Transportation' },
    
    // Manufacturing & Industrial
    { id: 'robotics', name: 'Robotics & Automation', emoji: '🦾', desc: 'Industrial robots, automation systems, and smart manufacturing', growth: 'Very High', marketSize: '$210B+', category: 'Industrial' },
    { id: 'manufacturing', name: 'Manufacturing & Industrial', emoji: '🏭', desc: 'Smart manufacturing, industrial IoT, and production optimization', growth: 'High', marketSize: '$500B+', category: 'Industrial' },
    { id: 'construction', name: 'Construction & Engineering', emoji: '🏗️', desc: 'Construction tech, project management, and engineering solutions', growth: 'Medium', marketSize: '$220B+', category: 'Industrial' },
    { id: 'iot_hardware', name: 'IoT & Hardware', emoji: '📡', desc: 'Internet of Things, connected devices, and hardware solutions', growth: 'High', marketSize: '$190B+', category: 'Industrial' },
    
    // Media & Entertainment
    { id: 'gaming', name: 'Gaming & Entertainment', emoji: '🎮', desc: 'Video games, streaming platforms, and interactive entertainment', growth: 'High', marketSize: '$190B+', category: 'Media' },
    { id: 'creator_economy', name: 'Creator Economy & Social', emoji: '📱', desc: 'Content creation tools, social platforms, and influencer tech', growth: 'Very High', marketSize: '$104B+', category: 'Media' },
    { id: 'media_content', name: 'Media & Content', emoji: '📺', desc: 'Digital media, content creation, and publishing platforms', growth: 'Medium', marketSize: '$120B+', category: 'Media' },
    { id: 'music_audio', name: 'Music & Audio', emoji: '🎵', desc: 'Music streaming, audio technology, and sound platforms', growth: 'Medium', marketSize: '$60B+', category: 'Media' },
    { id: 'virtual_reality', name: 'VR & AR', emoji: '🥽', desc: 'Virtual reality, augmented reality, and immersive technologies', growth: 'Very High', marketSize: '$85B+', category: 'Media' },
    
    // Education & Professional
    { id: 'edtech', name: 'EdTech & Learning', emoji: '📚', desc: 'Educational technology, online learning, and skill platforms', growth: 'High', marketSize: '$180B+', category: 'Education' },
    { id: 'hr_recruiting', name: 'HR & Recruiting', emoji: '👥', desc: 'Human resources tech, recruiting platforms, and workforce management', growth: 'High', marketSize: '$120B+', category: 'Professional' },
    { id: 'legal', name: 'Legal & Compliance', emoji: '⚖️', desc: 'LegalTech, compliance tools, and legal automation', growth: 'High', marketSize: '$80B+', category: 'Professional' },
    { id: 'productivity', name: 'Productivity & Workflow', emoji: '📋', desc: 'Productivity tools, workflow automation, and collaboration', growth: 'High', marketSize: '$90B+', category: 'Professional' },
    { id: 'communication', name: 'Communication & Collaboration', emoji: '💬', desc: 'Communication tools, video conferencing, and team collaboration', growth: 'High', marketSize: '$130B+', category: 'Professional' },
    { id: 'design_creative', name: 'Design & Creative Tools', emoji: '🎨', desc: 'Design software, creative platforms, and content creation tools', growth: 'High', marketSize: '$60B+', category: 'Professional' },
    
    // Agriculture & Food
    { id: 'food_agtech', name: 'Food & AgTech', emoji: '🌾', desc: 'Agricultural technology, food delivery, and sustainable farming', growth: 'High', marketSize: '$150B+', category: 'Agriculture' },
    { id: 'agriculture_farming', name: 'Agriculture & Farming', emoji: '🚜', desc: 'Precision agriculture, farm management, and crop technology', growth: 'High', marketSize: '$140B+', category: 'Agriculture' },
    
    // Marketing & Business
    { id: 'marketing', name: 'Marketing & Advertising', emoji: '📊', desc: 'MarTech, advertising platforms, and marketing automation', growth: 'High', marketSize: '$250B+', category: 'Business' },
    { id: 'data_analytics', name: 'Data & Analytics', emoji: '📈', desc: 'Big data, business intelligence, and analytics platforms', growth: 'Very High', marketSize: '$160B+', category: 'Business' },
    { id: 'api_infrastructure', name: 'API & Infrastructure', emoji: '🔗', desc: 'APIs, developer tools, and infrastructure services', growth: 'Very High', marketSize: '$110B+', category: 'Business' },
    
    // Consumer & Lifestyle
    { id: 'fashion', name: 'Fashion & Beauty', emoji: '👗', desc: 'Fashion tech, beauty platforms, and style recommendation engines', growth: 'Medium', marketSize: '$180B+', category: 'Consumer' },
    { id: 'sports_fitness', name: 'Sports & Fitness', emoji: '🏃', desc: 'Fitness apps, sports analytics, and wellness platforms', growth: 'High', marketSize: '$90B+', category: 'Consumer' },
    { id: 'travel_hospitality', name: 'Travel & Hospitality', emoji: '✈️', desc: 'Travel booking, hospitality tech, and tourism platforms', growth: 'Medium', marketSize: '$200B+', category: 'Consumer' },
    { id: 'pet_care', name: 'Pet Care & Services', emoji: '🐕', desc: 'Pet technology, veterinary services, and animal care', growth: 'High', marketSize: '$35B+', category: 'Consumer' },
    
    // Government & Social
    { id: 'government', name: 'Government & Civic Tech', emoji: '🏛️', desc: 'GovTech, civic engagement, and public sector solutions', growth: 'Medium', marketSize: '$70B+', category: 'Government' },
    { id: 'social_impact', name: 'Social Impact & NonProfit', emoji: '🤝', desc: 'Social good, nonprofit tech, and impact measurement', growth: 'Medium', marketSize: '$40B+', category: 'Social' },
    { id: 'security_surveillance', name: 'Security & Surveillance', emoji: '👁️', desc: 'Physical security, surveillance tech, and safety solutions', growth: 'High', marketSize: '$75B+', category: 'Security' }
  ];

  const POPULAR_INDUSTRIES = ALL_INDUSTRIES.slice(0, 12);

  const ANALYSIS_GOALS = [
    { id: 'opportunity', name: 'Market Opportunities', emoji: '🎯', desc: 'Discover untapped business potential' },
    { id: 'expansion', name: 'Geographic Expansion', emoji: '🌍', desc: 'Enter new markets strategically' },
    { id: 'investment', name: 'Investment Analysis', emoji: '💰', desc: 'Evaluate funding and investment potential' },
    { id: 'trends', name: 'Trend Forecasting', emoji: '📈', desc: 'Predict market movements and changes' },
    { id: 'competition', name: 'Competitive Intelligence', emoji: '🔍', desc: 'Analyze competitors and market positioning' },
    { id: 'timing', name: 'Market Timing', emoji: '⏰', desc: 'Optimize launch and entry timing' }
  ];

  const BUDGET_RANGES = [
    { id: 'bootstrap', name: 'Bootstrap', range: '$0 - $10K', emoji: '💡', desc: 'Self-funded startup' },
    { id: 'startup', name: 'Startup', range: '$10K - $100K', emoji: '🚀', desc: 'Early stage funding' },
    { id: 'growth', name: 'Growth', range: '$100K - $1M', emoji: '📈', desc: 'Scale-up phase' },
    { id: 'scale', name: 'Scale', range: '$1M - $10M', emoji: '⚡', desc: 'Rapid expansion' },
    { id: 'enterprise', name: 'Enterprise', range: '$10M+', emoji: '🏢', desc: 'Large scale operations' }
  ];
  // Load saved analyses
  const loadSavedAnalyses = async () => {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      const token = session?.access_token;
      
      if (!token) {
        logger.error('No auth token found');
        return;
      }

      const response = await fetch(`${BACKEND_URL}/api/global-market-intelligence/saved`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
      });

      if (response.ok) {
        const data = await response.json();
        setSavedAnalyses(data.analyses || []);
      }
    } catch (error) {
      logger.error('Failed to load saved analyses:', error);
    }
  };

  useEffect(() => {
    if (!authLoading && isAuthenticated && user) {
      loadSavedAnalyses();
    } else if (!authLoading && !isAuthenticated) {
      navigate('/login');
    }
  }, [authLoading, isAuthenticated, user, navigate]);

  const toggleSection = (section) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  const toggleAllSections = (expand) => {
    const newState = {};
    Object.keys(expandedSections).forEach(key => {
      newState[key] = expand;
    });
    setExpandedSections(newState);
  };

  const askFollowUp = async () => {
    if (!followUpQuestion.trim()) return;
    
    setFollowUpLoading(true);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      const token = session?.access_token;
      
      if (!token) {
        alert('Authentication required. Please log in again.');
        return;
      }
      
      const response = await fetch(`${BACKEND_URL}/api/global-market-intelligence/follow-up`, {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          originalAnalysis: results,
          question: followUpQuestion,
          context: { 
            industry: selectedIndustry, 
            country: selectedCountry 
          }
        })
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Follow-up request failed');
      }
      
      const data = await response.json();
      setFollowUpResponses([...followUpResponses, {
        question: followUpQuestion,
        answer: data.answer,
        timestamp: new Date().toISOString()
      }]);
      setFollowUpQuestion('');
    } catch (error) {
      logger.error('Follow-up error:', error);
      alert(`Failed to get follow-up answer: ${error.message}`);
    } finally {
      setFollowUpLoading(false);
    }
  };

  const saveAnalysis = async () => {
  try {
    const { data: { session } } = await supabase.auth.getSession();
    const token = session?.access_token;
    
    if (!token) {
      toast.error('Authentication required. Please log in again.');
      return;
    }

    setSaving(true); // Add this if you have a saving state

    const analysisToSave = {
      industry: selectedIndustry,
      country: selectedCountry,
      goal: analysisDetails.goal,
      budget: analysisDetails.budget,
      timeline: analysisDetails.timeline,
      results: results,
      followUpResponses: followUpResponses,
      confidenceScore: results?.confidence_score || 95
    };
    
    const response = await fetch(`${BACKEND_URL}/api/global-market-intelligence/saved`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify(analysisToSave)
    });

    if (!response.ok) {
      throw new Error('Failed to save analysis');
    }

    await loadSavedAnalyses();
    
    // Show beautiful popup instead of alert
    setShowSaveSuccess(true);
    setTimeout(() => setShowSaveSuccess(false), 3000);
    
  } catch (error) {
    logger.error('Save error:', error);
    toast.error('Failed to save analysis. Please try again.');
  } finally {
    setSaving(false); // Add this if you have a saving state
  }
};

  const loadSavedAnalysis = (analysis) => {
    setSelectedIndustry(analysis.industry);
    setSelectedCountry(analysis.country);
    setAnalysisDetails({
      goal: analysis.goal,
      budget: analysis.budget,
      timeline: analysis.timeline
    });
    setResults(analysis.results);
    setFollowUpResponses(analysis.followUpResponses || []);
    setCurrentStep('results');
    setShowSavedAnalyses(false);
  };

  const deleteSavedAnalysis = async (id) => {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      const token = session?.access_token;
      
      if (!token) {
        alert('Authentication required. Please log in again.');
        return;
      }

      const response = await fetch(`${BACKEND_URL}/api/global-market-intelligence/${id}`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        }
      });

      if (!response.ok) {
        throw new Error('Failed to delete analysis');
      }

      await loadSavedAnalyses();
    } catch (error) {
      logger.error('Delete error:', error);
      alert('Failed to delete analysis. Please try again.');
    }
  };

  const toggleCompareSelection = (id) => {
    setSelectedForCompare(prev => {
      if (prev.includes(id)) {
        return prev.filter(selectedId => selectedId !== id);
      } else {
        if (prev.length < 2) {
          return [...prev, id];
        } else {
          alert('You can only compare 2 analyses at a time.');
          return prev;
        }
      }
    });
  };

  const compareAnalyses = async () => {
    if (selectedForCompare.length !== 2) {
      alert('Please select exactly 2 analyses to compare.');
      return;
    }

    setCompareLoading(true);
    
    try {
      const analysis1 = savedAnalyses.find(a => a.id === selectedForCompare[0]);
      const analysis2 = savedAnalyses.find(a => a.id === selectedForCompare[1]);
      
      const { data: { session } } = await supabase.auth.getSession();
      const token = session?.access_token;
      
      if (!token) {
        alert('Authentication required. Please log in again.');
        return;
      }
      
      const response = await fetch(`${BACKEND_URL}/api/global-market-intelligence/compare`, {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          analysis1,
          analysis2
        })
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Comparison failed');
      }
      
      const data = await response.json();
      setCompareResults(data.comparison);
      setShowCompareResults(true);
      setShowSavedAnalyses(false);
      
    } catch (error) {
      logger.error('Comparison error:', error);
      alert(`Comparison Failed: ${error.message}`);
    } finally {
      setCompareLoading(false);
    }
  };

  const startAnalysis = async () => {
    setLoading(true);
    setCurrentStep('analysis');
    
    const loadingMessages = [
      '🌍 Searching real-time market data...',
      '🎯 Analyzing current competitors...',
      '💡 Identifying market opportunities...',
      '📊 Creating strategic recommendations...',
      '🚀 Building implementation roadmap...'
    ];

    try {
      const { data: { session } } = await supabase.auth.getSession();
      const token = session?.access_token;

      if (!token) {
        alert('Authentication required. Please log in again.');
        setLoading(false);
        setCurrentStep('details');
        return;
      }

      for (let i = 0; i < loadingMessages.length; i++) {
        setLoadingStep(i);
        setLoadingMessage(loadingMessages[i]);
        await new Promise(resolve => setTimeout(resolve, 1500));
      }

      const response = await fetch(`${BACKEND_URL}/api/global-market-intelligence/analyze`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          industry: selectedIndustry.id,
          country: selectedCountry.code,
          goal: analysisDetails.goal,
          budget: analysisDetails.budget,
          timeline: analysisDetails.timeline,
          question: analysisDetails.question
        })
      });

      if (!response.ok) {
        throw new Error('Analysis failed');
      }

      const data = await response.json();
      setResults(data.intelligence);
      setCurrentStep('results');

    } catch (error) {
      logger.error('Analysis error:', error);
      setResults(generateFallbackResults());
      setCurrentStep('results');
    } finally {
      setLoading(false);
    }
  };

  const generateFallbackResults = () => ({
    executive_summary: {
      opportunity_score: 92,
      market_attractiveness: 'Very High',
      key_insight: `${selectedIndustry.name} in ${selectedCountry.name} presents a ${selectedCountry.symbol}2.8B opportunity`,
      recommended_action: `Launch pilot program in ${selectedCountry.name} major cities within 6 months`,
      market_size_local: `${selectedCountry.symbol}2.8B`,
      competitive_intensity: 'Medium',
      regulatory_complexity: 'Low',
      growth_projection: '285% projected growth over 3 years'
    },
    innovation_opportunities: [
      {
        title: `🚀 ${selectedIndustry.name} Platform for ${selectedCountry.name}`,
        innovation_score: 94,
        market_size: `${selectedCountry.symbol}2.8B`,
        differentiation: `First comprehensive ${selectedIndustry.name.toLowerCase()} solution`,
        early_adopter_segments: ['Tech-forward enterprises', 'Digital natives'],
        mvp_timeline: '4-6 months',
        investment_needed: `${selectedCountry.symbol}300K - ${selectedCountry.symbol}500K`,
        technology_requirements: ['Cloud infrastructure', 'Mobile apps'],
        regulatory_hurdles: ['Data protection', 'Business registration'],
        success_probability: 87,
        competitive_moat: 'First-mover advantage',
        scalability_factor: 'High regional potential'
      }
    ],
    competitive_intelligence: {
      direct_competitors: [
        {
          name: 'Local Market Leader',
          market_share: '25%',
          strengths: ['Brand recognition', 'Local presence'],
          weaknesses: ['Limited innovation', 'High costs'],
          funding_status: 'Series B',
          employee_count: '200+',
          key_differentiators: ['Market presence'],
          recent_developments: 'Expansion plans',
          threat_level: 'High'
        }
      ],
      market_gaps: ['AI integration', 'Mobile-first approach', 'Affordable pricing'],
      barrier_to_entry_analysis: {
        capital_requirements: `${selectedCountry.symbol}500K`,
        regulatory_barriers: 'Medium',
        technical_barriers: 'Low',
        market_access_barriers: 'Medium',
        overall_barrier_height: 'Medium'
      }
    },
    market_dynamics: {
      market_size_breakdown: {
        total_addressable_market: `${selectedCountry.symbol}10B`,
        serviceable_addressable_market: `${selectedCountry.symbol}3B`,
        serviceable_obtainable_market: `${selectedCountry.symbol}500M`
      },
      growth_drivers: ['Digital transformation', 'Government initiatives', 'Consumer adoption'],
      market_headwinds: ['Economic uncertainty', 'Regulatory changes'],
      seasonality_patterns: 'Q4 peak demand',
      customer_behavior_trends: ['Mobile-first preference', 'Subscription models']
    },
    financial_projections: {
      year_1: { 
        revenue: `${selectedCountry.symbol}250K`, 
        customers: 50,
        gross_margin: '65%',
        burn_rate: `${selectedCountry.symbol}50K/month`
      },
      year_2: { 
        revenue: `${selectedCountry.symbol}1.5M`, 
        customers: 300,
        gross_margin: '70%',
        burn_rate: `${selectedCountry.symbol}100K/month`
      },
      year_3: { 
        revenue: `${selectedCountry.symbol}5.2M`, 
        customers: 1000,
        gross_margin: '75%',
        burn_rate: 'Profitable'
      },
      break_even_analysis: {
        break_even_month: 18,
        break_even_customers: 150,
        break_even_revenue: `${selectedCountry.symbol}100K/month`
      },
      exit_scenarios: [
        {
          type: 'Strategic Acquisition',
          timeline: '3-5 years',
          valuation_range: `${selectedCountry.symbol}50M - ${selectedCountry.symbol}100M`,
          potential_acquirers: ['Tech giants', 'Industry leaders'],
          likelihood: 'High'
        }
      ]
    },
    go_to_market_strategies: [
      {
        strategy_name: 'Enterprise-First Approach',
        target_segments: ['Large enterprises', 'Government'],
        primary_channels: ['Direct sales', 'Partnerships'],
        customer_acquisition_cost: `${selectedCountry.symbol}5K`,
        lifetime_value: `${selectedCountry.symbol}50K`,
        ltv_cac_ratio: '10:1',
        time_to_first_revenue: '3 months',
        time_to_positive_unit_economics: '6 months',
        scaling_potential: 'Very High',
        key_partnerships: ['System integrators', 'Tech partners'],
        launch_sequence: ['Pilot program', 'Beta launch', 'Full launch'],
        marketing_budget_allocation: {
          digital_marketing: '40%',
          local_partnerships: '30%',
          events_pr: '20%',
          content_creation: '10%'
        },
        success_metrics: ['100 enterprise clients', `${selectedCountry.symbol}100K MRR`]
      }
    ],
    risk_assessment: [
      {
        risk: 'Market Competition',
        category: 'Market',
        probability: 'High',
        impact: 'High',
        financial_impact: `${selectedCountry.symbol}1M`,
        timeline: '6-12 months',
        mitigation_strategy: 'Differentiation through innovation',
        contingency_plan: 'Pivot to niche market',
        monitoring_approach: 'Monthly competitive analysis'
      }
    ],
    implementation_roadmap: {
      phase_1: {
        name: 'Foundation & Validation',
        duration: '0-6 months',
        budget: `${selectedCountry.symbol}300K`,
        key_activities: ['Market research', 'MVP development', 'Regulatory approval'],
        success_metrics: ['500 beta users', 'Product-market fit'],
        team_required: ['Founders', 'Engineers', 'Sales'],
        key_milestones: ['Month 2: MVP', 'Month 4: Beta launch', 'Month 6: PMF'],
        critical_path: ['MVP development', 'Customer validation']
      },
      phase_2: {
        name: 'Market Entry & Growth',
        duration: '6-18 months',
        budget: `${selectedCountry.symbol}800K`,
        key_activities: ['Official launch', 'Customer acquisition', 'Team expansion'],
        success_metrics: ['5K users', `${selectedCountry.symbol}50K MRR`],
        team_required: ['Sales team', 'Marketing', 'Support'],
        key_milestones: ['Month 9: Launch', 'Month 12: Revenue target'],
        critical_path: ['Customer acquisition', 'Product stability']
      },
      phase_3: {
        name: 'Scale & Expansion',
        duration: '18-36 months',
        budget: `${selectedCountry.symbol}1.2M`,
        key_activities: ['Geographic expansion', 'Platform development'],
        success_metrics: ['50K users', `${selectedCountry.symbol}500K MRR`],
        team_required: ['Regional teams', 'Enterprise sales'],
        key_milestones: ['Month 24: Regional launch', 'Month 36: Exit readiness'],
        critical_path: ['Regional execution', 'Platform scaling']
      }
    },
    strategic_recommendations: [
      '🎯 Focus on enterprise customers for higher revenue',
      '🤝 Build strategic partnerships early',
      '📱 Prioritize mobile experience',
      '🌍 Plan regional expansion from day one',
      '💡 Invest in R&D for differentiation',
      '🔒 Ensure compliance and security',
      '📊 Track metrics obsessively',
      '🚀 Build for scale from the start'
    ],
    confidence_score: 92
  });

  const exportAsImage = async () => {
    try {
      const html2canvas = (await import('html2canvas')).default;
      
      if (resultsRef.current) {
        const canvas = await html2canvas(resultsRef.current, {
          backgroundColor: '#0f172a',
          scale: 2,
          useCORS: true,
          allowTaint: true
        });
        
        const link = document.createElement('a');
        link.download = `market-intelligence-${selectedIndustry?.name?.replace(/\s+/g, '-')}-${selectedCountry?.name?.replace(/\s+/g, '-')}.png`;
        link.href = canvas.toDataURL('image/png');
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      }
    } catch (error) {
      logger.error('Export failed:', error);
      alert('Export failed. Please try again.');
    }
  };

  const renderBreadcrumbs = () => (
    <div className="flex items-center gap-2 text-xs sm:text-sm text-slate-400 mb-4 sm:mb-6 overflow-x-auto pb-2">
      <button onClick={() => setCurrentStep('start')} className="hover:text-white transition-colors whitespace-nowrap">
        Start
      </button>
      {selectedIndustry && (
        <>
          <ChevronRight className="w-3 h-3 sm:w-4 sm:h-4 flex-shrink-0" />
          <button onClick={() => setCurrentStep('industry')} className="hover:text-white transition-colors whitespace-nowrap">
            {selectedIndustry.name}
          </button>
        </>
      )}
      {selectedCountry && (
        <>
          <ChevronRight className="w-3 h-3 sm:w-4 sm:h-4 flex-shrink-0" />
          <button onClick={() => setCurrentStep('country')} className="hover:text-white transition-colors whitespace-nowrap">
            {selectedCountry.name}
          </button>
        </>
      )}
      {currentStep === 'details' && (
        <>
          <ChevronRight className="w-3 h-3 sm:w-4 sm:h-4 flex-shrink-0" />
          <span className="whitespace-nowrap">Details</span>
        </>
      )}
      {currentStep === 'results' && (
        <>
          <ChevronRight className="w-3 h-3 sm:w-4 sm:h-4 flex-shrink-0" />
          <span className="whitespace-nowrap">Results</span>
        </>
      )}
    </div>
  );
  // RENDERING STARTS HERE

  if (authLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950 flex items-center justify-center p-4">
        <div className="text-center">
          <div className="w-16 h-16 sm:w-20 sm:h-20 mx-auto mb-6 sm:mb-8 relative">
            <div className="absolute inset-0 bg-blue-500/20 rounded-full animate-ping"></div>
            <Globe className="w-16 h-16 sm:w-20 sm:h-20 text-blue-500 relative z-10" />
          </div>
          <h2 className="text-xl sm:text-2xl font-bold text-white mb-2">Loading Market Intelligence</h2>
          <p className="text-sm sm:text-base text-slate-400">Verifying authentication...</p>
        </div>
      </div>
    );
  }

  // START SCREEN
  if (currentStep === 'start') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950">
        <nav className="border-b border-slate-800/50 backdrop-blur-xl bg-slate-900/50 sticky top-0 z-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex items-center justify-between h-14 sm:h-16">
              <button 
                onClick={() => navigate('/dashboard')}
                className="flex items-center gap-1 sm:gap-2 px-2 sm:px-4 py-2 bg-slate-800/50 border border-slate-700 rounded-lg sm:rounded-xl text-xs sm:text-sm text-slate-300 hover:bg-slate-700/50 hover:border-blue-600 hover:text-white transition-all"
              >
                <ArrowLeft className="w-3 h-3 sm:w-4 sm:h-4" />
                <span className="hidden xs:inline">Dashboard</span>
              </button>
              
              <div className="flex items-center gap-2 sm:gap-3">
                {savedAnalyses.length > 0 && (
                  <button
                    onClick={() => setShowSavedAnalyses(true)}
                    className="flex items-center gap-1 sm:gap-2 px-2 sm:px-4 py-2 bg-blue-600/10 border border-blue-600/30 text-blue-400 text-xs sm:text-sm font-medium rounded-lg sm:rounded-xl hover:bg-blue-600/20 transition-all"
                  >
                    <History className="w-3 h-3 sm:w-4 sm:h-4" />
                    <span className="hidden sm:inline">Saved</span>
                    <span>({savedAnalyses.length})</span>
                  </button>
                )}
                <div className="flex items-center gap-1 sm:gap-2 px-2 sm:px-3 py-2 bg-green-500/10 border border-green-500/30 rounded-lg sm:rounded-xl">
                  <div className="w-1.5 h-1.5 sm:w-2 sm:h-2 bg-green-500 rounded-full animate-pulse"></div>
                  <span className="text-xs sm:text-sm text-green-400">Live</span>
                </div>
              </div>
            </div>
          </div>
        </nav>

        <div className="relative overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-b from-blue-600/5 to-transparent"></div>
          <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 sm:py-16 md:py-20">
            <div className="text-center">
              <div className="inline-flex items-center justify-center w-16 h-16 sm:w-20 sm:h-20 md:w-24 md:h-24 mb-6 sm:mb-8 relative">
                <div className="absolute inset-0 bg-blue-600/20 rounded-full animate-pulse"></div>
                <Globe className="w-12 h-12 sm:w-14 sm:h-14 md:w-16 md:h-16 text-blue-500 relative z-10" />
              </div>
              
              <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold mb-4 sm:mb-6 px-4">
                <span className="bg-gradient-to-r from-blue-400 via-cyan-400 to-teal-400 bg-clip-text text-transparent">
                  Global Market Intelligence
                </span>
              </h1>
              
              <p className="text-base sm:text-lg md:text-xl text-slate-300 mb-8 sm:mb-12 max-w-3xl mx-auto px-4">
                Real-time AI analysis of 195+ countries and 65+ industries with live market data
              </p>
              
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 sm:gap-4 md:gap-6 mb-8 sm:mb-12 max-w-4xl mx-auto px-4">
                <div className="bg-slate-800/50 backdrop-blur border border-slate-700 rounded-xl sm:rounded-2xl p-4 sm:p-6">
                  <div className="text-2xl sm:text-3xl font-bold text-blue-400 mb-1 sm:mb-2">195+</div>
                  <div className="text-xs sm:text-sm text-slate-400">Countries</div>
                </div>
                <div className="bg-slate-800/50 backdrop-blur border border-slate-700 rounded-xl sm:rounded-2xl p-4 sm:p-6">
                  <div className="text-2xl sm:text-3xl font-bold text-cyan-400 mb-1 sm:mb-2">65+</div>
                  <div className="text-xs sm:text-sm text-slate-400">Industries</div>
                </div>
                <div className="bg-slate-800/50 backdrop-blur border border-slate-700 rounded-xl sm:rounded-2xl p-4 sm:p-6">
                  <div className="text-xl sm:text-2xl md:text-3xl font-bold text-teal-400 mb-1 sm:mb-2">Real-Time</div>
                  <div className="text-xs sm:text-sm text-slate-400">Market Data</div>
                </div>
                <div className="bg-slate-800/50 backdrop-blur border border-slate-700 rounded-xl sm:rounded-2xl p-4 sm:p-6">
                  <div className="text-2xl sm:text-3xl font-bold text-green-400 mb-1 sm:mb-2">AI</div>
                  <div className="text-xs sm:text-sm text-slate-400">Powered</div>
                </div>
              </div>
              
              <button
                onClick={() => setCurrentStep('industry')}
                className="inline-flex items-center gap-2 sm:gap-3 px-6 sm:px-8 py-3 sm:py-4 bg-gradient-to-r from-blue-600 to-cyan-600 text-white text-sm sm:text-base font-semibold rounded-xl sm:rounded-2xl hover:from-blue-700 hover:to-cyan-700 transform hover:scale-105 transition-all shadow-xl hover:shadow-2xl"
              >
                <Rocket className="w-4 h-4 sm:w-5 sm:h-5" />
                Start Analysis
                <ArrowRight className="w-4 h-4 sm:w-5 sm:h-5" />
              </button>
            </div>
          </div>
        </div>

        {/* Saved Analyses Modal */}
        {showSavedAnalyses && (
          <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-50 p-4">
            <div className="bg-slate-900 border border-slate-800 rounded-xl sm:rounded-2xl max-w-5xl w-full max-h-[90vh] sm:max-h-[80vh] overflow-hidden">
              <div className="p-4 sm:p-6 border-b border-slate-800">
                <div className="flex items-center justify-between gap-3">
                  <h2 className="text-xl sm:text-2xl font-bold text-white">Saved Analyses</h2>
                  <div className="flex items-center gap-2 sm:gap-3">
                    {compareMode && (
                      <button
                        onClick={compareAnalyses}
                        disabled={selectedForCompare.length !== 2 || compareLoading}
                        className="px-3 sm:px-4 py-2 bg-green-600/20 text-green-400 text-xs sm:text-sm rounded-lg hover:bg-green-600/30 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
                      >
                        {compareLoading ? 'Comparing...' : `Compare (${selectedForCompare.length}/2)`}
                      </button>
                    )}
                    <button
                      onClick={() => {
                        setCompareMode(!compareMode);
                        setSelectedForCompare([]);
                      }}
                      className="px-3 sm:px-4 py-2 bg-blue-600/20 text-blue-400 text-xs sm:text-sm rounded-lg hover:bg-blue-600/30 transition-all"
                    >
                      {compareMode ? 'Cancel' : 'Compare'}
                    </button>
                    <button
                      onClick={() => setShowSavedAnalyses(false)}
                      className="p-2 hover:bg-slate-800 rounded-lg transition-colors"
                    >
                      <X className="w-4 h-4 sm:w-5 sm:h-5 text-slate-400" />
                    </button>
                  </div>
                </div>
              </div>
              
              <div className="p-4 sm:p-6 overflow-y-auto max-h-[calc(90vh-80px)] sm:max-h-[calc(80vh-80px)]">
                {savedAnalyses.length === 0 ? (
                  <p className="text-center text-sm sm:text-base text-slate-400 py-8 sm:py-12 px-4">
                    No saved analyses yet. Complete an analysis and save it for future reference.
                  </p>
                ) : (
                  <div className="space-y-3 sm:space-y-4">
                    {savedAnalyses.map(analysis => (
                      <div key={analysis.id} className="p-4 sm:p-5 bg-slate-800/50 border border-slate-700 rounded-lg sm:rounded-xl hover:bg-slate-800/70 transition-all">
                        <div className="flex flex-col sm:flex-row items-start justify-between gap-3">
                          <div className="flex-1 min-w-0 w-full">
                            <div className="flex items-start gap-2 sm:gap-3 mb-2">
                              {compareMode && (
                                <input
                                  type="checkbox"
                                  checked={selectedForCompare.includes(analysis.id)}
                                  onChange={() => toggleCompareSelection(analysis.id)}
                                  className="mt-1 w-4 h-4 sm:w-5 sm:h-5 rounded border-slate-600 bg-slate-700 text-blue-600 focus:ring-blue-500 flex-shrink-0"
                                />
                              )}
                              <h3 className="text-base sm:text-lg font-semibold text-white break-words">
                                {analysis.industry.name} in {analysis.country.name}
                              </h3>
                            </div>
                            <div className="flex flex-wrap gap-2 sm:gap-4 text-xs sm:text-sm text-slate-400">
                              <span>Score: {analysis.confidenceScore}%</span>
                              <span className="break-words">Goal: {ANALYSIS_GOALS.find(g => g.id === analysis.goal)?.name}</span>
                              <span className="break-words">Budget: {BUDGET_RANGES.find(b => b.id === analysis.budget)?.range}</span>
                              <span className="whitespace-nowrap">{new Date(analysis.created_at).toLocaleDateString()}</span>
                            </div>
                          </div>
                          <div className="flex gap-2 w-full sm:w-auto">
                            <button
                              onClick={() => loadSavedAnalysis(analysis)}
                              className="flex-1 sm:flex-none px-3 sm:px-4 py-2 bg-blue-600/20 text-blue-400 text-xs sm:text-sm rounded-lg hover:bg-blue-600/30 transition-all"
                            >
                              Load
                            </button>
                            <button
                              onClick={() => deleteSavedAnalysis(analysis.id)}
                              className="flex-1 sm:flex-none px-3 sm:px-4 py-2 bg-red-600/20 text-red-400 text-xs sm:text-sm rounded-lg hover:bg-red-600/30 transition-all"
                            >
                              Delete
                            </button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Compare Results Modal */}
{showCompareResults && (
  <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-50 p-4">
    <div className="bg-slate-900 border border-slate-800 rounded-xl sm:rounded-2xl max-w-5xl w-full max-h-[90vh] sm:max-h-[80vh] overflow-hidden">
      <div className="p-4 sm:p-6 border-b border-slate-800">
        <div className="flex items-center justify-between">
          <h2 className="text-xl sm:text-2xl font-bold text-white">Analysis Comparison</h2>
          <button
            onClick={() => {
              setShowCompareResults(false);
              setCompareResults(null);
            }}
            className="p-2 hover:bg-slate-800 rounded-lg transition-colors"
          >
            <X className="w-4 h-4 sm:w-5 sm:h-5 text-slate-400" />
          </button>
        </div>
      </div>
      
      <div className="p-4 sm:p-6 overflow-y-auto max-h-[calc(90vh-80px)] sm:max-h-[calc(80vh-80px)]">
        <div className="space-y-3">
          {compareResults && compareResults.split('\n').map((line, idx) => {
            const trimmedLine = line.trim();
            
            // Skip empty lines
            if (!trimmedLine) {
              return <div key={idx} className="h-2" />;
            }
            
            // H1 Headers (# )
            if (trimmedLine.startsWith('# ')) {
              return (
                <h1 key={idx} className="text-2xl sm:text-3xl font-bold text-white mt-8 mb-4 pb-3 border-b border-slate-700 break-words">
                  {trimmedLine.substring(2)}
                </h1>
              );
            }
            
            // H2 Headers (## )
            if (trimmedLine.startsWith('## ')) {
              return (
                <h2 key={idx} className="text-xl sm:text-2xl font-bold text-blue-400 mt-6 mb-3 flex items-center gap-2 break-words">
                  {trimmedLine.substring(3)}
                </h2>
              );
            }
            
            // H3 Headers (### )
            if (trimmedLine.startsWith('### ')) {
              return (
                <h3 key={idx} className="text-lg sm:text-xl font-semibold text-green-400 mt-4 mb-2 break-words">
                  {trimmedLine.substring(4)}
                </h3>
              );
            }
            
            // Horizontal rules (---)
            if (trimmedLine === '---') {
              return <hr key={idx} className="my-6 border-slate-700" />;
            }
            
            // Table rows (contains |)
            if (trimmedLine.includes('|')) {
              const cells = trimmedLine.split('|').map(cell => cell.trim()).filter(cell => cell);
              const isHeaderRow = cells.some(cell => cell.includes('---'));
              
              if (isHeaderRow) {
                return null; // Skip separator rows
              }
              
              const isFirstRow = trimmedLine.startsWith('|') && !compareResults.split('\n')[idx - 1]?.includes('---');
              
              return (
                <div 
                  key={idx} 
                  className={`grid grid-cols-${cells.length} gap-2 sm:gap-4 ${
                    isFirstRow ? 'bg-slate-800/70 font-semibold text-white' : 'bg-slate-800/30 text-slate-300'
                  } rounded-lg p-3 border border-slate-700/50`}
                >
                  {cells.map((cell, i) => (
                    <div key={i} className="text-xs sm:text-sm break-words">
                      {cell.replace(/\*\*/g, '')}
                    </div>
                  ))}
                </div>
              );
            }
            
            // Unordered lists (- or *)
            if (trimmedLine.startsWith('- ') || trimmedLine.startsWith('* ')) {
              const content = trimmedLine.substring(2);
              const processed = content.replace(/\*\*(.*?)\*\*/g, '<strong class="text-white font-semibold">$1</strong>');
              
              return (
                <div key={idx} className="flex items-start gap-2 sm:gap-3 ml-4 my-1.5">
                  <span className="text-blue-400 text-lg mt-0.5 flex-shrink-0">•</span>
                  <span 
                    className="text-slate-300 text-sm sm:text-base flex-1 break-words"
                    dangerouslySetInnerHTML={{ __html: processed }}
                  />
                </div>
              );
            }
            
            // Numbered lists (1. 2. etc)
            if (/^\d+\./.test(trimmedLine)) {
              const processed = trimmedLine.replace(/\*\*(.*?)\*\*/g, '<strong class="text-white font-semibold">$1</strong>');
              
              return (
                <div key={idx} className="flex items-start gap-2 sm:gap-3 ml-4 my-1.5">
                  <span 
                    className="text-slate-300 text-sm sm:text-base break-words"
                    dangerouslySetInnerHTML={{ __html: processed }}
                  />
                </div>
              );
            }
            
            // Bold standalone lines (**text**)
            if (trimmedLine.startsWith('**') && trimmedLine.endsWith('**')) {
              return (
                <p key={idx} className="font-bold text-white my-3 text-sm sm:text-base break-words">
                  {trimmedLine.substring(2, trimmedLine.length - 2)}
                </p>
              );
            }
            
            // Regular paragraphs with inline formatting
            let processed = trimmedLine;
            
            // Process bold text
            processed = processed.replace(/\*\*(.*?)\*\*/g, '<strong class="text-white font-semibold">$1</strong>');
            
            // Process italic text
            processed = processed.replace(/\*(.*?)\*/g, '<em class="text-slate-200">$1</em>');
            
            // Check if line has emojis
            const hasEmoji = /[\u{1F300}-\u{1F9FF}]/u.test(trimmedLine);
            
            return (
              <p 
                key={idx} 
                className={`text-slate-300 leading-relaxed my-2 text-sm sm:text-base break-words ${
                  hasEmoji ? 'text-base sm:text-lg' : ''
                }`}
                dangerouslySetInnerHTML={{ __html: processed }}
              />
            );
          })}
        </div>
      </div>
    </div>
  </div>
        )}
      </div>
    );
  }

  // INDUSTRY SELECTION SCREEN
  if (currentStep === 'industry') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950">
        <nav className="border-b border-slate-800/50 backdrop-blur-xl bg-slate-900/50 sticky top-0 z-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex items-center justify-between h-14 sm:h-16">
              <button 
                onClick={() => setCurrentStep('start')}
                className="flex items-center gap-1 sm:gap-2 px-2 sm:px-4 py-2 bg-slate-800/50 border border-slate-700 rounded-lg sm:rounded-xl text-xs sm:text-sm text-slate-300 hover:bg-slate-700/50 hover:border-blue-600 hover:text-white transition-all"
              >
                <ArrowLeft className="w-3 h-3 sm:w-4 sm:h-4" />
                <span>Back</span>
              </button>
              
              <div className="flex items-center gap-1.5 sm:gap-2">
                <div className="w-6 sm:w-8 h-1 bg-blue-600 rounded"></div>
                <div className="w-6 sm:w-8 h-1 bg-slate-700 rounded"></div>
                <div className="w-6 sm:w-8 h-1 bg-slate-700 rounded"></div>
                <span className="ml-2 sm:ml-3 text-xs sm:text-sm text-slate-400 whitespace-nowrap">Step 1 of 3</span>
              </div>
            </div>
          </div>
        </nav>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-12">
          {renderBreadcrumbs()}
          
          <div className="text-center mb-8 sm:mb-12">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-white mb-3 sm:mb-4 px-4">Choose Your Industry</h2>
            <p className="text-sm sm:text-base md:text-lg text-slate-400 px-4">Select from 65+ industry verticals</p>
          </div>

          <div className="max-w-2xl mx-auto mb-6 sm:mb-8">
            <div className="relative">
              <Search className="absolute left-3 sm:left-4 top-1/2 -translate-y-1/2 w-4 h-4 sm:w-5 sm:h-5 text-slate-400" />
              <input
                type="text"
                placeholder="Search industries..."
                value={industrySearchQuery}
                onChange={(e) => setIndustrySearchQuery(e.target.value)}
                className="w-full pl-10 sm:pl-12 pr-3 sm:pr-4 py-3 sm:py-4 bg-slate-800/50 border border-slate-700 rounded-xl sm:rounded-2xl text-sm sm:text-base text-white placeholder-slate-400 focus:border-blue-600 focus:outline-none focus:ring-2 focus:ring-blue-600/20 transition-all"
              />
            </div>
          </div>

          <div className="mb-6 sm:mb-8">
            <h3 className="text-base sm:text-lg font-semibold text-slate-300 mb-3 sm:mb-4 px-2">Popular Industries</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3 sm:gap-4">
              {POPULAR_INDUSTRIES.filter(ind => 
                ind.name.toLowerCase().includes(industrySearchQuery.toLowerCase()) ||
                ind.desc.toLowerCase().includes(industrySearchQuery.toLowerCase())
              ).map((industry) => (
                <button
                  key={industry.id}
                  onClick={() => {
                    setSelectedIndustry(industry);
                    setCurrentStep('country');
                  }}
                  className="group p-4 sm:p-6 bg-slate-800/50 backdrop-blur border border-slate-700 rounded-xl sm:rounded-2xl hover:bg-slate-700/50 hover:border-blue-600 hover:scale-105 transition-all text-left"
                >
                  <div className="text-2xl sm:text-3xl mb-2 sm:mb-3">{industry.emoji}</div>
                  <h4 className="font-semibold text-sm sm:text-base text-white mb-1 sm:mb-2 group-hover:text-blue-400 transition-colors break-words">
                    {industry.name}
                  </h4>
                  <p className="text-xs sm:text-sm text-slate-400 mb-2 sm:mb-3 line-clamp-2">{industry.desc}</p>
                  <div className="flex items-center justify-between gap-2">
                    <span className={`text-xs px-2 py-1 rounded-full whitespace-nowrap ${
                      industry.growth === 'Extreme' ? 'bg-red-500/20 text-red-400' :
                      industry.growth === 'Very High' ? 'bg-orange-500/20 text-orange-400' :
                      industry.growth === 'High' ? 'bg-green-500/20 text-green-400' :
                      'bg-slate-600/20 text-slate-400'
                    }`}>
                      {industry.growth}
                    </span>
                    <span className="text-xs font-bold text-slate-300 truncate">{industry.marketSize}</span>
                  </div>
                </button>
              ))}
            </div>
          </div>

          <div className="relative">
            <button
              onClick={() => setShowIndustryDropdown(!showIndustryDropdown)}
              className="w-full flex items-center justify-between px-4 sm:px-6 py-3 sm:py-4 bg-slate-800/50 border border-slate-700 rounded-xl sm:rounded-2xl hover:bg-slate-700/50 transition-all"
            >
              <span className="text-sm sm:text-base text-white font-medium">Browse All {ALL_INDUSTRIES.length} Industries</span>
              <ChevronDown className={`w-4 h-4 sm:w-5 sm:h-5 text-slate-400 transition-transform ${showIndustryDropdown ? 'rotate-180' : ''}`} />
            </button>
            
            {showIndustryDropdown && (
              <div className="absolute top-full mt-2 w-full max-h-72 sm:max-h-96 overflow-y-auto bg-slate-900/95 backdrop-blur border border-slate-700 rounded-xl sm:rounded-2xl shadow-2xl z-10">
                {Object.entries(
                  ALL_INDUSTRIES.reduce((acc, industry) => {
                    const category = industry.category || 'Other';
                    if (!acc[category]) acc[category] = [];
                    acc[category].push(industry);
                    return acc;
                  }, {})
                ).map(([category, industries]) => (
                  <div key={category}>
                    <div className="px-3 sm:px-4 py-2 bg-slate-800/50 text-xs sm:text-sm font-semibold text-slate-400 sticky top-0 backdrop-blur">
                      {category}
                    </div>
                    {industries.filter(ind => 
                      ind.name.toLowerCase().includes(industrySearchQuery.toLowerCase()) ||
                      ind.desc.toLowerCase().includes(industrySearchQuery.toLowerCase())
                    ).map(industry => (
                      <button
                        key={industry.id}
                        onClick={() => {
                          setSelectedIndustry(industry);
                          setShowIndustryDropdown(false);
                          setCurrentStep('country');
                        }}
                        className="w-full px-3 sm:px-4 py-2 sm:py-3 flex items-center gap-2 sm:gap-3 hover:bg-slate-800/50 transition-all text-left"
                      >
                        <span className="text-xl sm:text-2xl flex-shrink-0">{industry.emoji}</span>
                        <div className="flex-1 min-w-0">
                          <div className="text-sm sm:text-base text-white font-medium truncate">{industry.name}</div>
                          <div className="text-xs text-slate-400 line-clamp-1">{industry.desc}</div>
                        </div>
                        <div className="text-right flex-shrink-0">
                          <div className="text-xs font-bold text-slate-300 whitespace-nowrap">{industry.marketSize}</div>
                          <div className={`text-xs whitespace-nowrap ${
                            industry.growth === 'Extreme' ? 'text-red-400' :
                            industry.growth === 'Very High' ? 'text-orange-400' :
                            industry.growth === 'High' ? 'text-green-400' :
                            'text-slate-400'
                          }`}>
                            {industry.growth}
                          </div>
                        </div>
                      </button>
                    ))}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }
// COUNTRY SELECTION SCREEN
  if (currentStep === 'country') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950">
        <nav className="border-b border-slate-800/50 backdrop-blur-xl bg-slate-900/50 sticky top-0 z-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex items-center justify-between h-14 sm:h-16">
              <button 
                onClick={() => setCurrentStep('industry')}
                className="flex items-center gap-1 sm:gap-2 px-2 sm:px-4 py-2 bg-slate-800/50 border border-slate-700 rounded-lg sm:rounded-xl text-xs sm:text-sm text-slate-300 hover:bg-slate-700/50 hover:border-blue-600 hover:text-white transition-all"
              >
                <ArrowLeft className="w-3 h-3 sm:w-4 sm:h-4" />
                <span>Back</span>
              </button>
              
              <div className="flex items-center gap-1.5 sm:gap-2">
                <div className="w-6 sm:w-8 h-1 bg-blue-600 rounded"></div>
                <div className="w-6 sm:w-8 h-1 bg-blue-600 rounded"></div>
                <div className="w-6 sm:w-8 h-1 bg-slate-700 rounded"></div>
                <span className="ml-2 sm:ml-3 text-xs sm:text-sm text-slate-400 whitespace-nowrap">Step 2 of 3</span>
              </div>
            </div>
          </div>
        </nav>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-12">
          {renderBreadcrumbs()}
          
          <div className="text-center mb-8 sm:mb-12">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-white mb-3 sm:mb-4 px-4">Select Target Market</h2>
            <p className="text-sm sm:text-base md:text-lg text-slate-400 px-4">
              Choose from 195+ countries for <span className="text-blue-400 font-semibold">{selectedIndustry?.name}</span>
            </p>
          </div>

          <div className="max-w-2xl mx-auto mb-6 sm:mb-8">
            <div className="relative">
              <Search className="absolute left-3 sm:left-4 top-1/2 -translate-y-1/2 w-4 h-4 sm:w-5 sm:h-5 text-slate-400" />
              <input
                type="text"
                placeholder="Search countries..."
                value={countrySearchQuery}
                onChange={(e) => setCountrySearchQuery(e.target.value)}
                className="w-full pl-10 sm:pl-12 pr-3 sm:pr-4 py-3 sm:py-4 bg-slate-800/50 border border-slate-700 rounded-xl sm:rounded-2xl text-sm sm:text-base text-white placeholder-slate-400 focus:border-blue-600 focus:outline-none focus:ring-2 focus:ring-blue-600/20 transition-all"
              />
            </div>
          </div>

          <div className="mb-6 sm:mb-8">
            <h3 className="text-base sm:text-lg font-semibold text-slate-300 mb-3 sm:mb-4 px-2">Major Markets</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3 sm:gap-4">
              {ALL_COUNTRIES.slice(0, 12).filter(country =>
                country.name.toLowerCase().includes(countrySearchQuery.toLowerCase()) ||
                country.region.toLowerCase().includes(countrySearchQuery.toLowerCase())
              ).map((country) => (
                <button
                  key={country.code}
                  onClick={() => {
                    setSelectedCountry(country);
                    setCurrentStep('details');
                  }}
                  className="group p-4 sm:p-6 bg-slate-800/50 backdrop-blur border border-slate-700 rounded-xl sm:rounded-2xl hover:bg-slate-700/50 hover:border-blue-600 hover:scale-105 transition-all text-left"
                >
                  <div className="flex items-start gap-2 sm:gap-3 mb-2 sm:mb-3">
                    <span className="text-2xl sm:text-3xl flex-shrink-0">{country.flag}</span>
                    <div className="flex-1 min-w-0">
                      <h4 className="font-semibold text-sm sm:text-base text-white group-hover:text-blue-400 transition-colors break-words">
                        {country.name}
                      </h4>
                      <p className="text-xs sm:text-sm text-slate-400 truncate">{country.region}</p>
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-2 text-xs mb-2 sm:mb-3">
                    <div>
                      <span className="text-slate-500">GDP:</span>
                      <span className="text-slate-300 ml-1 font-medium">{country.gdp}</span>
                    </div>
                    <div>
                      <span className="text-slate-500">Pop:</span>
                      <span className="text-slate-300 ml-1 font-medium">{country.population}</span>
                    </div>
                  </div>
                  <div className="flex items-center justify-between gap-2">
                    <span className="text-xs text-slate-400 truncate">
                      {country.currency} ({country.symbol})
                    </span>
                    <span className={`text-xs px-2 py-1 rounded-full whitespace-nowrap ${
                      country.ease === 'Very Easy' || country.ease === 'Easy' ? 'bg-green-500/20 text-green-400' :
                      country.ease === 'Medium' ? 'bg-yellow-500/20 text-yellow-400' :
                      'bg-red-500/20 text-red-400'
                    }`}>
                      {country.ease}
                    </span>
                  </div>
                </button>
              ))}
            </div>
          </div>

          <div className="relative">
            <button
              onClick={() => setShowCountryDropdown(!showCountryDropdown)}
              className="w-full flex items-center justify-between px-4 sm:px-6 py-3 sm:py-4 bg-slate-800/50 border border-slate-700 rounded-xl sm:rounded-2xl hover:bg-slate-700/50 transition-all"
            >
              <span className="text-sm sm:text-base text-white font-medium">Browse All {ALL_COUNTRIES.length} Countries</span>
              <ChevronDown className={`w-4 h-4 sm:w-5 sm:h-5 text-slate-400 transition-transform ${showCountryDropdown ? 'rotate-180' : ''}`} />
            </button>
            
            {showCountryDropdown && (
              <div className="absolute top-full mt-2 w-full max-h-72 sm:max-h-96 overflow-y-auto bg-slate-900/95 backdrop-blur border border-slate-700 rounded-xl sm:rounded-2xl shadow-2xl z-10">
                {Object.entries(
                  ALL_COUNTRIES.reduce((acc, country) => {
                    const region = country.region;
                    if (!acc[region]) acc[region] = [];
                    acc[region].push(country);
                    return acc;
                  }, {})
                ).map(([region, countries]) => (
                  <div key={region}>
                    <div className="px-3 sm:px-4 py-2 bg-slate-800/50 text-xs sm:text-sm font-semibold text-slate-400 sticky top-0 backdrop-blur">
                      {region}
                    </div>
                    {countries
                      .filter(country =>
                        country.name.toLowerCase().includes(countrySearchQuery.toLowerCase()) ||
                        country.region.toLowerCase().includes(countrySearchQuery.toLowerCase())
                      )
                      .map(country => (
                        <button
                          key={country.code}
                          onClick={() => {
                            setSelectedCountry(country);
                            setShowCountryDropdown(false);
                            setCurrentStep('details');
                          }}
                          className="w-full px-3 sm:px-4 py-2 sm:py-3 flex items-center gap-2 sm:gap-3 hover:bg-slate-800/50 transition-all text-left"
                        >
                          <span className="text-xl sm:text-2xl flex-shrink-0">{country.flag}</span>
                          <div className="flex-1 min-w-0">
                            <div className="text-sm sm:text-base text-white font-medium truncate">{country.name}</div>
                            <div className="text-xs text-slate-400 truncate">{country.currency} ({country.symbol})</div>
                          </div>
                          <div className="text-right flex-shrink-0">
                            <div className="text-xs text-slate-400 whitespace-nowrap">GDP: {country.gdp}</div>
                            <div className="text-xs text-slate-400 whitespace-nowrap">Pop: {country.population}</div>
                            <div className={`text-xs whitespace-nowrap ${
                              country.ease === 'Very Easy' || country.ease === 'Easy' ? 'text-green-400' :
                              country.ease === 'Medium' ? 'text-yellow-400' :
                              'text-red-400'
                            }`}>
                              {country.ease}
                            </div>
                          </div>
                        </button>
                      ))}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }

  // DETAILS SCREEN
  if (currentStep === 'details') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950">
        <nav className="border-b border-slate-800/50 backdrop-blur-xl bg-slate-900/50 sticky top-0 z-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex items-center justify-between h-14 sm:h-16">
              <button 
                onClick={() => setCurrentStep('country')}
                className="flex items-center gap-1 sm:gap-2 px-2 sm:px-4 py-2 bg-slate-800/50 border border-slate-700 rounded-lg sm:rounded-xl text-xs sm:text-sm text-slate-300 hover:bg-slate-700/50 hover:border-blue-600 hover:text-white transition-all"
              >
                <ArrowLeft className="w-3 h-3 sm:w-4 sm:h-4" />
                <span>Back</span>
              </button>
              
              <div className="flex items-center gap-1.5 sm:gap-2">
                <div className="w-6 sm:w-8 h-1 bg-blue-600 rounded"></div>
                <div className="w-6 sm:w-8 h-1 bg-blue-600 rounded"></div>
                <div className="w-6 sm:w-8 h-1 bg-blue-600 rounded"></div>
                <span className="ml-2 sm:ml-3 text-xs sm:text-sm text-slate-400 whitespace-nowrap">Step 3 of 3</span>
              </div>
            </div>
          </div>
        </nav>

        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-12">
          {renderBreadcrumbs()}
          
          <div className="text-center mb-8 sm:mb-12">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-white mb-3 sm:mb-4 px-4">Analysis Details</h2>
            <div className="flex flex-wrap items-center justify-center gap-2 sm:gap-3 text-sm sm:text-base md:text-lg px-4">
              <span className="text-slate-400">Analyzing</span>
              <span className="text-blue-400 font-semibold break-words">{selectedIndustry?.name}</span>
              <span className="text-slate-400">in</span>
              <span className="flex items-center gap-1 sm:gap-2 break-words">
                <span className="text-xl sm:text-2xl">{selectedCountry?.flag}</span>
                <span className="text-cyan-400 font-semibold">{selectedCountry?.name}</span>
              </span>
            </div>
          </div>

          <div className="bg-slate-800/30 backdrop-blur border border-slate-700 rounded-2xl sm:rounded-3xl p-6 sm:p-8">
            <div className="mb-8 sm:mb-10">
              <h3 className="text-lg sm:text-xl font-semibold text-white mb-4 sm:mb-6">What's your primary goal?</h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                {ANALYSIS_GOALS.map((goal) => (
                  <button
                    key={goal.id}
                    onClick={() => setAnalysisDetails({...analysisDetails, goal: goal.id})}
                    className={`p-4 sm:p-5 rounded-xl sm:rounded-2xl border-2 transition-all text-left ${
                      analysisDetails.goal === goal.id 
                        ? 'bg-blue-600/20 border-blue-600 shadow-lg shadow-blue-600/20' 
                        : 'bg-slate-800/50 border-slate-700 hover:border-slate-600'
                    }`}
                  >
                    <div className="flex items-start gap-2 sm:gap-3">
                      <span className="text-xl sm:text-2xl flex-shrink-0">{goal.emoji}</span>
                      <div>
                        <div className="font-semibold text-sm sm:text-base text-white mb-1">{goal.name}</div>
                        <div className="text-xs sm:text-sm text-slate-400">{goal.desc}</div>
                      </div>
                    </div>
                  </button>
                ))}
              </div>
            </div>

            <div className="mb-8 sm:mb-10">
              <h3 className="text-lg sm:text-xl font-semibold text-white mb-4 sm:mb-6">What's your budget range?</h3>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-3 sm:gap-4">
                {BUDGET_RANGES.map((budget) => (
                  <button
                    key={budget.id}
                    onClick={() => setAnalysisDetails({...analysisDetails, budget: budget.id})}
                    className={`p-4 sm:p-5 rounded-xl sm:rounded-2xl border-2 transition-all ${
                      analysisDetails.budget === budget.id 
                        ? 'bg-blue-600/20 border-blue-600 shadow-lg shadow-blue-600/20' 
                        : 'bg-slate-800/50 border-slate-700 hover:border-slate-600'
                    }`}
                  >
                    <div className="text-xl sm:text-2xl mb-1 sm:mb-2">{budget.emoji}</div>
                    <div className="font-semibold text-white text-xs sm:text-sm mb-1">{budget.name}</div>
                    <div className="text-xs text-slate-400">{budget.range}</div>
                  </button>
                ))}
              </div>
            </div>

            <div className="mb-8 sm:mb-10">
              <h3 className="text-lg sm:text-xl font-semibold text-white mb-4 sm:mb-6">What's your timeline?</h3>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 sm:gap-4">
                {['Immediate', '3-6 months', '6-12 months', '1+ years'].map((timeline) => (
                  <button
                    key={timeline}
                    onClick={() => setAnalysisDetails({...analysisDetails, timeline})}
                    className={`p-3 sm:p-4 rounded-xl sm:rounded-2xl border-2 transition-all ${
                      analysisDetails.timeline === timeline 
                        ? 'bg-blue-600/20 border-blue-600 shadow-lg shadow-blue-600/20' 
                        : 'bg-slate-800/50 border-slate-700 hover:border-slate-600'
                    }`}
                  >
                    <div className="font-semibold text-sm sm:text-base text-white">{timeline}</div>
                  </button>
                ))}
              </div>
            </div>

            <div className="mb-8 sm:mb-10">
              <h3 className="text-lg sm:text-xl font-semibold text-white mb-4 sm:mb-6">Any specific questions? (Optional)</h3>
              <textarea
                placeholder="e.g., What are the regulatory requirements? Who are the main competitors?"
                value={analysisDetails.question || ''}
                onChange={(e) => setAnalysisDetails({...analysisDetails, question: e.target.value})}
                className="w-full p-3 sm:p-4 bg-slate-800/50 border border-slate-700 rounded-xl sm:rounded-2xl text-sm sm:text-base text-white placeholder-slate-400 focus:border-blue-600 focus:outline-none focus:ring-2 focus:ring-blue-600/20 transition-all resize-vertical min-h-[100px] sm:min-h-[120px]"
                rows={4}
              />
            </div>

            <div className="text-center">
              <button
                onClick={startAnalysis}
                disabled={!analysisDetails.goal || !analysisDetails.budget || !analysisDetails.timeline}
                className="inline-flex items-center gap-2 sm:gap-3 px-6 sm:px-8 py-3 sm:py-4 bg-gradient-to-r from-blue-600 to-cyan-600 text-white text-sm sm:text-base font-semibold rounded-xl sm:rounded-2xl hover:from-blue-700 hover:to-cyan-700 disabled:from-slate-600 disabled:to-slate-700 disabled:cursor-not-allowed transform hover:scale-105 disabled:scale-100 transition-all shadow-xl hover:shadow-2xl"
              >
                <Brain className="w-4 h-4 sm:w-5 sm:h-5" />
                Generate Intelligence
                <Sparkles className="w-4 h-4 sm:w-5 sm:h-5" />
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // ANALYSIS LOADING SCREEN
  if (currentStep === 'analysis') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950 flex items-center justify-center p-4">
        <div className="max-w-2xl w-full">
          <div className="text-center mb-8 sm:mb-12">
            <div className="inline-flex items-center justify-center w-20 h-20 sm:w-24 sm:h-24 mb-6 sm:mb-8 relative">
              <div className="absolute inset-0 bg-blue-600/20 rounded-full animate-ping"></div>
              <Brain className="w-14 h-14 sm:w-16 sm:h-16 text-blue-500 relative z-10 animate-pulse" />
            </div>
            
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-white mb-3 sm:mb-4 px-4">
              Analyzing {selectedCountry?.name} Market
            </h2>
            <p className="text-base sm:text-lg text-slate-400 px-4">
              Real-time analysis for {selectedIndustry?.name}
            </p>
          </div>

          <div className="bg-slate-800/30 backdrop-blur border border-slate-700 rounded-2xl sm:rounded-3xl p-6 sm:p-8">
            <div className="space-y-3 sm:space-y-4 mb-6 sm:mb-8">
              {['🌍 Searching real-time market data...', '🎯 Analyzing current competitors...', '💡 Identifying market opportunities...', '📊 Creating strategic recommendations...', '🚀 Building implementation roadmap...'].map((step, index) => (
                <div
                  key={index}
                  className={`flex items-center gap-3 sm:gap-4 p-3 sm:p-4 rounded-lg sm:rounded-xl transition-all ${
                    index < loadingStep 
                      ? 'bg-green-600/10 border border-green-600/30'
                      : index === loadingStep 
                        ? 'bg-blue-600/10 border border-blue-600/30 animate-pulse' 
                        : 'bg-slate-700/30 border border-slate-700'
                  }`}
                >
                  <div className={`w-7 h-7 sm:w-8 sm:h-8 rounded-full flex items-center justify-center text-xs sm:text-sm font-bold flex-shrink-0 ${
                    index < loadingStep 
                      ? 'bg-green-600 text-white' 
                      : index === loadingStep 
                        ? 'bg-blue-600 text-white animate-pulse' 
                        : 'bg-slate-700 text-slate-400'
                  }`}>
                    {index < loadingStep ? '✓' : index === loadingStep ? 
                      <div className="w-3 h-3 sm:w-4 sm:h-4 border-2 border-white border-t-transparent rounded-full animate-spin" /> 
                      : index + 1}
                  </div>
                  <span className={`font-medium text-left flex-1 text-xs sm:text-sm md:text-base break-words ${
                    index <= loadingStep ? 'text-white' : 'text-slate-400'
                  }`}>
                    {step}
                  </span>
                </div>
              ))}
            </div>

            {loadingMessage && (
              <div className="text-center text-xs sm:text-sm text-slate-400 mb-3 sm:mb-4">
                {loadingMessage}
              </div>
            )}

            <div className="mt-6 sm:mt-8">
              <div className="w-full h-2 bg-slate-700 rounded-full overflow-hidden">
                <div 
                  className="h-full bg-gradient-to-r from-blue-600 to-cyan-600 transition-all duration-500"
                  style={{ width: `${((loadingStep + 1) / 5) * 100}%` }}
                />
              </div>
              <p className="text-xs sm:text-sm text-slate-400 mt-2 sm:mt-3 text-center">
                {Math.round(((loadingStep + 1) / 5) * 100)}% Complete
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }
// RESULTS SCREEN - COMPLETE WITH ALL SECTIONS
  if (currentStep === 'results') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950">
        <nav className="border-b border-slate-800/50 backdrop-blur-xl bg-slate-900/50 sticky top-0 z-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex items-center justify-between h-14 sm:h-16 gap-3">
              <button 
                onClick={() => {
                  setCurrentStep('start');
                  setSelectedIndustry(null);
                  setSelectedCountry(null);
                  setAnalysisDetails({});
                  setResults(null);
                  setFollowUpResponses([]);
                  setFollowUpQuestion('');
                }}
                className="flex items-center gap-1 sm:gap-2 px-2 sm:px-4 py-2 bg-slate-800/50 border border-slate-700 rounded-lg sm:rounded-xl text-xs sm:text-sm text-slate-300 hover:bg-slate-700/50 hover:border-blue-600 hover:text-white transition-all whitespace-nowrap"
              >
                <ArrowLeft className="w-3 h-3 sm:w-4 sm:h-4" />
                <span className="hidden xs:inline">New Analysis</span>
              </button>
              
              <div className="flex items-center gap-2 sm:gap-3">
                <button
                  onClick={() => toggleAllSections(!expandedSections.overview)}
                  className="hidden md:flex items-center gap-2 px-3 sm:px-4 py-2 bg-slate-700/50 border border-slate-600 text-slate-300 text-xs sm:text-sm rounded-lg sm:rounded-xl hover:bg-slate-600/50 transition-all"
                >
                  {expandedSections.overview ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                  <span>{expandedSections.overview ? 'Collapse' : 'Expand'}</span>
                </button>
                <button
                  onClick={saveAnalysis}
                  className="flex items-center gap-1 sm:gap-2 px-2 sm:px-4 py-2 bg-green-600/10 border border-green-600/30 text-green-400 text-xs sm:text-sm rounded-lg sm:rounded-xl hover:bg-green-600/20 transition-all"
                >
                  <Save className="w-3 h-3 sm:w-4 sm:h-4" />
                  <span className="hidden sm:inline">Save</span>
                </button>
                <button
                  onClick={exportAsImage}
                  className="flex items-center gap-1 sm:gap-2 px-2 sm:px-4 py-2 bg-blue-600/10 border border-blue-600/30 text-blue-400 text-xs sm:text-sm rounded-lg sm:rounded-xl hover:bg-blue-600/20 transition-all"
                >
                  <Download className="w-3 h-3 sm:w-4 sm:h-4" />
                  <span className="hidden sm:inline">Export</span>
                </button>
              </div>
            </div>
          </div>
        </nav>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-8" ref={resultsRef}>
          {renderBreadcrumbs()}
          

{/* Hero Metrics Section */}
<div className="bg-gradient-to-r from-blue-600/10 via-cyan-600/10 to-teal-600/10 border border-blue-600/30 rounded-2xl sm:rounded-3xl p-6 sm:p-8 md:p-12 mb-6 sm:mb-8">
  <div className="text-center mb-6 sm:mb-8 md:mb-10">
    <div className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl xl:text-8xl font-bold bg-gradient-to-r from-green-400 to-emerald-400 bg-clip-text text-transparent mb-3 sm:mb-4 md:mb-6">
      {results?.executive_summary?.opportunity_score || 92}%
    </div>
    
    <h1 className="text-xl sm:text-2xl md:text-3xl lg:text-4xl xl:text-5xl font-bold text-white mb-2 sm:mb-3 md:mb-4 px-4">
      {results?.executive_summary?.market_attractiveness || 'Very High'} Opportunity
    </h1>
    
    <div className="flex flex-wrap items-center justify-center gap-2 sm:gap-3 text-sm sm:text-base md:text-lg lg:text-xl px-4">
      <span className="text-slate-400">Analysis for</span>
      <span className="text-blue-400 font-semibold break-words">{selectedIndustry?.name}</span>
      <span className="text-slate-400">in</span>
      <span className="flex items-center gap-1 sm:gap-2 break-words">
        <span className="text-xl sm:text-2xl">{selectedCountry?.flag}</span>
        <span className="text-cyan-400 font-semibold">{selectedCountry?.name}</span>
      </span>
    </div>
  </div>

  {/* Detailed Market Size Breakdown - More detail */}
{results?.executive_summary?.market_size_details && (
  <div className="bg-slate-800/30 backdrop-blur rounded-xl sm:rounded-2xl p-4 sm:p-5 mb-6 sm:mb-8">
    <h4 className="text-sm font-semibold text-slate-300 mb-4 flex items-center gap-2">
      <BarChart className="w-4 h-4 text-blue-400" />
      Market Size Breakdown
    </h4>
     {/* Main description */}
    <div className="text-sm text-slate-300 leading-relaxed mb-4">
      {results.executive_summary.market_size_details}
    </div>
      <div className="text-sm text-slate-300 leading-relaxed break-words">
        {results.executive_summary.market_size_details}
      </div>
    </div>
  )}

  {/* Enhanced Summary Cards - Better mobile sizing */}
<div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 mb-6 sm:mb-8">
  {/* Market Size Card */}
  <div className="bg-gradient-to-br from-blue-600/20 to-cyan-600/20 border border-blue-600/30 backdrop-blur rounded-xl p-3 sm:p-4 text-center hover:scale-105 transition-transform">
    <div className="w-8 h-8 sm:w-10 sm:h-10 bg-blue-600/30 rounded-lg flex items-center justify-center mx-auto mb-2">
      <DollarSign className="w-4 h-4 sm:w-5 sm:h-5 text-blue-400" />
    </div>
    <div className="text-lg sm:text-xl md:text-2xl font-bold text-blue-400 mb-1 break-words leading-tight">
      {(() => {
        const marketSize = results?.executive_summary?.market_size_local || `${selectedCountry?.symbol}2.8B`;
        const match = marketSize.match(/[€$£¥₹₩₽₴C]+[\d,.]+[BMK]/);
        return match ? match[0] : marketSize.split(' ')[0];
      })()}
    </div>
    <div className="text-xs text-slate-400 font-medium">Market Size</div>
  </div>
  
  {/* Growth Rate Card */}
  <div className="bg-gradient-to-br from-purple-600/20 to-pink-600/20 border border-purple-600/30 backdrop-blur rounded-xl p-3 sm:p-4 text-center hover:scale-105 transition-transform">
    <div className="w-8 h-8 sm:w-10 sm:h-10 bg-purple-600/30 rounded-lg flex items-center justify-center mx-auto mb-2">
      <TrendingUp className="w-4 h-4 sm:w-5 sm:h-5 text-purple-400" />
    </div>
    <div className="text-lg sm:text-xl md:text-2xl font-bold text-purple-400 mb-1">
      {(() => {
        const growth = results?.executive_summary?.growth_projection || '17.3%';
        const match = growth.match(/\d+\.?\d*%/);
        return match ? match[0] : growth.split(' ')[0];
      })()}
    </div>
    <div className="text-xs text-slate-400 font-medium">Growth Rate</div>
  </div>
  
  {/* Competition Card - SMALLER TEXT */}
  <div className="bg-gradient-to-br from-orange-600/20 to-red-600/20 border border-orange-600/30 backdrop-blur rounded-xl p-3 sm:p-4 text-center hover:scale-105 transition-transform">
    <div className="w-8 h-8 sm:w-10 sm:h-10 bg-orange-600/30 rounded-lg flex items-center justify-center mx-auto mb-2">
      <Users className="w-4 h-4 sm:w-5 sm:h-5 text-orange-400" />
    </div>
    <div className="text-sm sm:text-base md:text-lg font-bold text-orange-400 mb-1 leading-tight">
      {results?.executive_summary?.competitive_intensity || 'Medium'}
    </div>
    <div className="text-xs text-slate-400 font-medium">Competition</div>
  </div>
  
  {/* Break-even Card */}
  <div className="bg-gradient-to-br from-green-600/20 to-emerald-600/20 border border-green-600/30 backdrop-blur rounded-xl p-3 sm:p-4 text-center hover:scale-105 transition-transform">
    <div className="w-8 h-8 sm:w-10 sm:h-10 bg-green-600/30 rounded-lg flex items-center justify-center mx-auto mb-2">
      <Timer className="w-4 h-4 sm:w-5 sm:h-5 text-green-400" />
    </div>
    <div className="text-lg sm:text-xl md:text-2xl font-bold text-green-400 mb-1">
      {results?.financial_projections?.break_even_analysis?.break_even_month || '18'}
      <span className="text-sm ml-0.5">mo</span>
    </div>
    <div className="text-xs text-slate-400 font-medium">Break-even</div>
  </div>
</div>

  {/* Key Insight Box */}
  <div className="bg-slate-800/30 backdrop-blur rounded-xl sm:rounded-2xl p-4 sm:p-5 md:p-6">
    <div className="flex flex-col sm:flex-row items-start gap-3">
      <Lightbulb className="w-5 h-5 sm:w-6 sm:h-6 text-yellow-500 flex-shrink-0 mt-1" />
      <div className="flex-1 min-w-0">
        <h3 className="text-base sm:text-lg font-semibold text-white mb-2">Key Insight</h3>
        <p className="text-sm sm:text-base text-slate-200 leading-relaxed break-words">
          {results?.executive_summary?.key_insight}
        </p>
      </div>
    </div>
  </div>

  {/* Recommended Action */}
  <div className="mt-4 sm:mt-6 text-center">
    <div className="inline-flex flex-col sm:flex-row items-center gap-2 sm:gap-3 px-4 sm:px-6 py-2 sm:py-3 bg-green-500/20 border border-green-500/30 rounded-lg sm:rounded-xl max-w-full">
      <CheckCircle className="w-4 h-4 sm:w-5 sm:h-5 text-green-400 flex-shrink-0" />
      <span className="text-xs sm:text-sm md:text-base text-green-300 font-medium text-center break-words">
        {results?.executive_summary?.recommended_action}
      </span>
    </div>
  </div>
</div>

          {/* Real-time Search Indicator */}
          {results?.real_time_search && (
            <div className="flex items-center justify-center mb-4 sm:mb-6">
              <div className="inline-flex items-center gap-2 px-3 sm:px-4 py-2 bg-green-500/10 border border-green-500/30 rounded-lg sm:rounded-xl">
                <Search className="w-3 h-3 sm:w-4 sm:h-4 text-green-500" />
                <span className="text-xs sm:text-sm text-green-300 font-medium">
                  Analysis based on real-time market data
                </span>
              </div>
            </div>
          )}

          {/* ALL EXPANDABLE SECTIONS START HERE */}
<div className="space-y-3 sm:space-y-4">
  {/* Market Overview Section */}
  <div className="bg-slate-800/30 backdrop-blur border border-slate-700 rounded-xl sm:rounded-2xl overflow-hidden">
    <button
      onClick={() => toggleSection('overview')}
      className="w-full p-4 sm:p-6 flex items-center justify-between hover:bg-slate-800/50 transition-all"
    >
      <div className="flex items-center gap-2 sm:gap-3">
        <Globe className="w-5 h-5 sm:w-6 sm:h-6 text-blue-500 flex-shrink-0" />
        <div className="text-left">
          <h3 className="text-base sm:text-lg md:text-xl font-bold text-white">Market Overview</h3>
          <p className="text-xs sm:text-sm text-slate-400 mt-1">Strategic summary and key metrics</p>
        </div>
      </div>
      {expandedSections.overview ? <ChevronUp className="w-4 h-4 sm:w-5 sm:h-5 text-slate-400 flex-shrink-0" /> : <ChevronDown className="w-4 h-4 sm:w-5 sm:h-5 text-slate-400 flex-shrink-0" />}
    </button>
    
    {expandedSections.overview && (
  <div className="px-4 sm:px-6 pb-4 sm:pb-6">
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6">
      {/* Growth Drivers */}
      <div className="bg-slate-700/30 rounded-lg sm:rounded-xl p-4 sm:p-5">
        <h4 className="font-semibold text-sm sm:text-base text-white mb-3 flex items-center gap-2">
          <TrendingUp className="w-4 h-4 sm:w-5 sm:h-5 text-green-400" />
          Growth Drivers
        </h4>
        <div className="space-y-2">
          {(results?.market_dynamics?.growth_drivers || []).map((driver, idx) => (
            <div key={idx} className="flex items-start gap-2 p-2 bg-green-600/10 border border-green-600/30 rounded text-xs sm:text-sm text-slate-200">
              <CheckCircle className="w-4 h-4 text-green-400 mt-0.5 flex-shrink-0" />
              <span className="break-words">{driver}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Market Headwinds */}
      <div className="bg-slate-700/30 rounded-lg sm:rounded-xl p-4 sm:p-5">
        <h4 className="font-semibold text-sm sm:text-base text-white mb-3 flex items-center gap-2">
          <AlertTriangle className="w-4 h-4 sm:w-5 sm:h-5 text-orange-400" />
          Market Headwinds
        </h4>
        <div className="space-y-2">
          {(results?.market_dynamics?.market_headwinds || []).map((headwind, idx) => (
            <div key={idx} className="flex items-start gap-2 p-2 bg-orange-600/10 border border-orange-600/30 rounded text-xs sm:text-sm text-slate-200">
              <AlertTriangle className="w-4 h-4 text-orange-400 mt-0.5 flex-shrink-0" />
              <span className="break-words">{headwind}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Customer Behavior Trends */}
      <div className="bg-slate-700/30 rounded-lg sm:rounded-xl p-4 sm:p-5">
        <h4 className="font-semibold text-sm sm:text-base text-white mb-3 flex items-center gap-2">
          <Users className="w-4 h-4 sm:w-5 sm:h-5 text-cyan-400" />
          Customer Behavior Trends
        </h4>
        <div className="space-y-2">
          {(results?.market_dynamics?.customer_behavior_trends || []).map((trend, idx) => (
            <div key={idx} className="flex items-start gap-2 p-2 bg-cyan-600/10 border border-cyan-600/30 rounded text-xs sm:text-sm text-slate-200">
              <TrendingUp className="w-4 h-4 text-cyan-400 mt-0.5 flex-shrink-0" />
              <span className="break-words">{trend}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Seasonality & Macro Trends */}
      <div className="bg-slate-700/30 rounded-lg sm:rounded-xl p-4 sm:p-5">
        <h4 className="font-semibold text-sm sm:text-base text-white mb-3 flex items-center gap-2">
          <Calendar className="w-4 h-4 sm:w-5 sm:h-5 text-purple-400" />
          Seasonality & Macro Trends
        </h4>
        <div className="space-y-3">
          {results?.market_dynamics?.seasonality_patterns && (
            <div className="p-2 bg-purple-600/10 border border-purple-600/30 rounded">
              <div className="text-xs text-purple-400 mb-1 font-semibold">Seasonality</div>
              <div className="text-xs sm:text-sm text-slate-200 break-words">
                {results.market_dynamics.seasonality_patterns}
              </div>
            </div>
          )}
          {results?.market_dynamics?.macro_trends && (
            <div className="p-2 bg-blue-600/10 border border-blue-600/30 rounded">
              <div className="text-xs text-blue-400 mb-1 font-semibold">Macro Trends</div>
              <div className="text-xs sm:text-sm text-slate-200 break-words">
                {results.market_dynamics.macro_trends}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  </div>
    )}
  </div>

{/* Innovation Opportunities */}
<div className="bg-slate-800/30 backdrop-blur border border-slate-700 rounded-xl sm:rounded-2xl overflow-hidden">
  <button
    onClick={() => toggleSection('opportunities')}
    className="w-full p-4 sm:p-6 flex items-center justify-between hover:bg-slate-800/50 transition-all"
  >
    <div className="flex items-center gap-2 sm:gap-3">
      <Rocket className="w-5 h-5 sm:w-6 sm:h-6 text-orange-500 flex-shrink-0" />
      <div className="text-left">
        <h3 className="text-base sm:text-lg md:text-xl font-bold text-white">Innovation Opportunities</h3>
        <p className="text-xs sm:text-sm text-slate-400 mt-1">
          {results?.innovation_opportunities?.length || 3} high-potential opportunities identified
        </p>
      </div>
    </div>
    {expandedSections.opportunities ? <ChevronUp className="w-4 h-4 sm:w-5 sm:h-5 text-slate-400 flex-shrink-0" /> : <ChevronDown className="w-4 h-4 sm:w-5 sm:h-5 text-slate-400 flex-shrink-0" />}
  </button>
  
  {expandedSections.opportunities && (
    <div className="px-4 sm:px-6 pb-4 sm:pb-6 space-y-3 sm:space-y-4">
      {(results?.innovation_opportunities || []).map((opp, idx) => (
        <div key={idx} className={`bg-gradient-to-r ${
          idx === 0 ? 'from-orange-600/10 to-amber-600/10 border-orange-600/30' :
          idx === 1 ? 'from-blue-600/10 to-cyan-600/10 border-blue-600/30' :
          'from-purple-600/10 to-pink-600/10 border-purple-600/30'
        } border rounded-lg sm:rounded-xl p-4 sm:p-6`}>
          <div className="flex flex-col sm:flex-row items-start justify-between gap-3 mb-4">
            <h4 className="text-base sm:text-lg font-semibold text-white flex-1 break-words">{opp.title}</h4>
            <span className={`px-3 py-1 ${
              idx === 0 ? 'bg-orange-600/20 text-orange-300' :
              idx === 1 ? 'bg-blue-600/20 text-blue-300' :
              'bg-purple-600/20 text-purple-300'
            } text-xs sm:text-sm font-bold rounded-full whitespace-nowrap`}>
              {opp.innovation_score}% Score
            </span>
          </div>
          
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 sm:gap-4 mb-4">
            <div>
              <div className="text-xs text-slate-400 mb-1">Market Size</div>
              <div className="text-xs sm:text-sm font-semibold text-white break-words">{opp.market_size}</div>
            </div>
            <div>
              <div className="text-xs text-slate-400 mb-1">Timeline</div>
              <div className="text-xs sm:text-sm font-semibold text-white">{opp.mvp_timeline}</div>
            </div>
            <div>
              <div className="text-xs text-slate-400 mb-1">Investment</div>
              <div className="text-xs sm:text-sm font-semibold text-white break-words">{opp.investment_needed}</div>
            </div>
            <div>
              <div className="text-xs text-slate-400 mb-1">Success Rate</div>
              <div className="text-xs sm:text-sm font-semibold text-green-400">{opp.success_probability}%</div>
            </div>
          </div>
          
          <p className="text-xs sm:text-sm text-slate-200 leading-relaxed mb-4 break-words">{opp.differentiation}</p>
          
          {/* Execution Roadmap */}
          {opp.execution_roadmap && (
            <div className="mb-4 p-3 bg-slate-700/30 rounded-lg">
              <div className="text-xs text-slate-400 mb-2">Execution Roadmap</div>
              <div className="text-xs sm:text-sm text-slate-200 break-words leading-relaxed">
                {opp.execution_roadmap}
              </div>
            </div>
          )}

          {/* Scalability Factor */}
          {opp.scalability_factor && (
            <div className="mb-4 p-3 bg-green-600/10 border border-green-600/30 rounded-lg">
              <div className="text-xs text-green-400 mb-2 font-semibold">Scalability</div>
              <div className="text-xs sm:text-sm text-slate-200 break-words">
                {opp.scalability_factor}
              </div>
            </div>
          )}
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
            <div>
              <div className="text-xs text-slate-400 mb-2">Target Segments</div>
              {opp.early_adopter_segments && opp.early_adopter_segments.length > 0 ? (
                <div className="flex flex-wrap gap-2">
                  {opp.early_adopter_segments.map((segment, i) => (
                    <span key={i} className="px-2 py-1 bg-blue-600/20 text-blue-300 text-xs rounded-full break-words">
                      {segment}
                    </span>
                  ))}
                </div>
              ) : (
                <p className="text-xs text-slate-500 italic">No segments identified yet</p>
              )}
            </div>
            <div>
              <div className="text-xs text-slate-400 mb-2">Tech Requirements</div>
              {opp.technology_requirements && opp.technology_requirements.length > 0 ? (
                <div className="flex flex-wrap gap-2">
                  {opp.technology_requirements.map((tech, i) => (
                    <span key={i} className="px-2 py-1 bg-purple-600/20 text-purple-300 text-xs rounded-full break-words">
                      {tech}
                    </span>
                  ))}
                </div>
              ) : (
                <p className="text-xs text-slate-500 italic">No requirements specified yet</p>
              )}
            </div>
          </div>

          {/* Regulatory Hurdles */}
          {opp.regulatory_hurdles && opp.regulatory_hurdles.length > 0 && (
            <div className="mb-4">
              <div className="text-xs text-slate-400 mb-2">Regulatory Hurdles</div>
              <div className="space-y-1">
                {opp.regulatory_hurdles.map((hurdle, i) => (
                  <div key={i} className="flex items-start gap-2 p-2 bg-red-600/10 border border-red-600/30 rounded text-xs text-slate-200">
                    <AlertTriangle className="w-3 h-3 text-red-400 mt-0.5 flex-shrink-0" />
                    <span className="break-words">{hurdle}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
          
          {/* Competitive Moat */}
          {opp.competitive_moat && (
            <div className="p-3 bg-slate-700/30 rounded-lg">
              <div className="text-xs text-slate-400 mb-1">Competitive Moat</div>
              <div className="text-xs sm:text-sm text-white break-words">{opp.competitive_moat}</div>
            </div>
          )}
        </div>
      ))}
    </div>
  )}
</div>

 {/* Go-to-Market Strategies */}
<div className="bg-slate-800/30 backdrop-blur border border-slate-700 rounded-xl sm:rounded-2xl overflow-hidden">
  <button
    onClick={() => toggleSection('goToMarket')}
    className="w-full p-4 sm:p-6 flex items-center justify-between hover:bg-slate-800/50 transition-all"
  >
    <div className="flex items-center gap-2 sm:gap-3">
      <Target className="w-5 h-5 sm:w-6 sm:h-6 text-purple-500 flex-shrink-0" />
      <div className="text-left">
        <h3 className="text-base sm:text-lg md:text-xl font-bold text-white">Go-to-Market Strategies</h3>
        <p className="text-xs sm:text-sm text-slate-400 mt-1">Strategic approaches for market entry</p>
      </div>
    </div>
    {expandedSections.goToMarket ? <ChevronUp className="w-4 h-4 sm:w-5 sm:h-5 text-slate-400 flex-shrink-0" /> : <ChevronDown className="w-4 h-4 sm:w-5 sm:h-5 text-slate-400 flex-shrink-0" />}
  </button>
  
  {expandedSections.goToMarket && (
    <div className="px-4 sm:px-6 pb-4 sm:pb-6 space-y-3 sm:space-y-4">
      {(results?.go_to_market_strategies || []).map((strategy, idx) => (
        <div key={idx} className="bg-purple-600/10 border border-purple-600/30 rounded-lg sm:rounded-xl p-4 sm:p-6">
          <h4 className="text-base sm:text-lg font-semibold text-white mb-3 break-words">{strategy.strategy_name}</h4>
          
          {/* Strategy Rationale */}
          {strategy.strategy_rationale && (
            <div className="mb-4 p-3 sm:p-4 bg-blue-600/10 border border-blue-600/30 rounded-lg">
              <div className="text-xs text-blue-400 mb-2 font-semibold">Why This Strategy Works</div>
              <div className="text-xs sm:text-sm text-slate-200 break-words leading-relaxed">{strategy.strategy_rationale}</div>
            </div>
          )}
          {/* CAC, LTV, and other metrics with explanations */}
<div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 mb-4">
  {/* CAC */}
  <div className="bg-slate-700/30 rounded-lg p-3">
    <div className="text-xs text-slate-400 mb-1">CAC</div>
    <div className="text-base sm:text-lg font-bold text-white mb-2">{strategy.customer_acquisition_cost}</div>
    <div className="text-xs text-slate-300 leading-relaxed">
      Cost to acquire one customer through {strategy.primary_channels?.[0]?.split(':')[0] || 'primary channels'}
    </div>
  </div>
  
  {/* LTV */}
  <div className="bg-slate-700/30 rounded-lg p-3">
    <div className="text-xs text-slate-400 mb-1">LTV</div>
    <div className="text-base sm:text-lg font-bold text-white mb-2">{strategy.lifetime_value}</div>
    <div className="text-xs text-slate-300 leading-relaxed">
      Total revenue expected from one customer over their lifetime
    </div>
  </div>
  
  {/* LTV/CAC Ratio with explanation */}
  <div className="bg-green-600/10 border border-green-600/30 rounded-lg p-3">
    <div className="text-xs text-green-400 mb-1 font-semibold">LTV/CAC Ratio</div>
    <div className="text-base sm:text-lg font-bold text-green-300 mb-2">
      {strategy.ltv_cac_ratio?.split(' ')[0] || '5:1'}
    </div>
    <div className="text-xs text-green-200 leading-relaxed">
      For every ${strategy.customer_acquisition_cost?.replace(/[^\d]/g, '') || '1'} spent, you earn ${(parseInt(strategy.customer_acquisition_cost?.replace(/[^\d]/g, '') || 1000) * 5).toLocaleString()}
    </div>
  </div>
  
  {/* First Revenue */}
  <div className="bg-slate-700/30 rounded-lg p-3">
    <div className="text-xs text-slate-400 mb-1">First Revenue</div>
    <div className="text-base sm:text-lg font-bold text-white mb-2">{strategy.time_to_first_revenue}</div>
    <div className="text-xs text-slate-300 leading-relaxed">
      Time until you generate your first paying customer
    </div>
  </div>
</div>

{/* How to achieve these numbers */}
<div className="bg-blue-600/10 border border-blue-600/30 rounded-lg p-4 mb-4">
  <div className="text-sm font-semibold text-blue-300 mb-2">💡 How to Achieve These Numbers</div>
  <div className="text-xs sm:text-sm text-slate-200 leading-relaxed">
    {strategy.strategy_rationale || `To reach these metrics: Start with ${strategy.primary_channels?.[0] || 'targeted marketing'}, convert at estimated rates, and optimize over ${strategy.time_to_positive_unit_economics || '6 months'} to improve unit economics.`}
  </div>
</div>
          
          {/* Customer Profile */}
          {strategy.customer_profile && (
            <div className="mb-4 p-3 sm:p-4 bg-slate-700/30 rounded-lg">
              <div className="text-xs text-slate-400 mb-2">Ideal Customer Profile</div>
              <div className="text-xs sm:text-sm text-slate-200 break-words leading-relaxed">{strategy.customer_profile}</div>
            </div>
          )}
          
          {/* Target Segments */}
          {strategy.target_segments && strategy.target_segments.length > 0 && (
            <div className="mb-4">
              <div className="text-xs text-slate-400 mb-2">Target Segments</div>
              <div className="flex flex-wrap gap-2">
                {strategy.target_segments.map((segment, i) => (
                  <span key={i} className="px-3 py-1.5 bg-purple-600/20 text-purple-300 text-xs sm:text-sm rounded-lg break-words">
                    {segment}
                  </span>
                ))}
              </div>
            </div>
          )}
          
          {/* Primary Channels */}
          {strategy.primary_channels && strategy.primary_channels.length > 0 && (
            <div className="mb-4">
              <div className="text-xs text-slate-400 mb-2">Primary Channels</div>
              <div className="space-y-2">
                {strategy.primary_channels.map((channel, i) => (
                  <div key={i} className="flex items-start gap-2 bg-slate-700/30 rounded-lg p-2.5">
                    <CheckCircle className="w-4 h-4 text-green-400 mt-0.5 flex-shrink-0" />
                    <span className="text-xs sm:text-sm text-slate-200 break-words">{channel}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
          
          {/* Launch Sequence */}
          {strategy.launch_sequence && strategy.launch_sequence.length > 0 && (
            <div className="mb-4 p-3 sm:p-4 bg-slate-800/50 rounded-lg">
              <div className="text-xs text-slate-400 mb-3">Launch Sequence</div>
              <div className="space-y-2">
                {strategy.launch_sequence.map((phase, i) => (
                  <div key={i} className="flex items-start gap-2">
                    <div className="flex-shrink-0 w-5 h-5 rounded-full bg-purple-600/30 text-purple-300 flex items-center justify-center text-xs font-bold">
                      {i + 1}
                    </div>
                    <span className="text-xs sm:text-sm text-slate-200 break-words flex-1">{phase}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
          
          {/* Key Partnerships */}
          {strategy.key_partnerships && strategy.key_partnerships.length > 0 && (
            <div className="mb-4">
              <div className="text-xs text-slate-400 mb-2">Strategic Partnerships</div>
              <div className="space-y-2">
                {strategy.key_partnerships.map((partner, i) => (
                  <div key={i} className="flex items-start gap-2 bg-green-600/10 border border-green-600/30 rounded-lg p-2.5">
                    <Users className="w-4 h-4 text-green-400 mt-0.5 flex-shrink-0" />
                    <span className="text-xs sm:text-sm text-slate-200 break-words">{partner}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
          
          {/* Marketing Budget Allocation */}
          {strategy.marketing_budget_allocation && (
            <div className="mb-4 bg-slate-700/30 rounded-lg p-3 sm:p-4">
              <div className="text-xs text-slate-400 mb-3">Marketing Budget Allocation</div>
              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-3">
                {Object.entries(strategy.marketing_budget_allocation).map(([key, value]) => (
                  <div key={key} className="bg-slate-800/50 rounded-lg p-2.5">
                    <div className="text-xs text-slate-500 mb-1 capitalize break-words">
                      {key.replace(/_/g, ' ')}
                    </div>
                    <div className="text-xs sm:text-sm font-bold text-white break-words">{value}</div>
                  </div>
                ))}
              </div>
            </div>
          )}
          
          {/* Success Metrics */}
          {strategy.success_metrics && strategy.success_metrics.length > 0 && (
            <div className="mb-4 p-3 sm:p-4 bg-green-600/10 border border-green-600/30 rounded-lg">
              <div className="text-xs text-green-400 mb-3 font-semibold">Success Metrics & Milestones</div>
              <div className="space-y-2">
                {strategy.success_metrics.map((metric, i) => (
                  <div key={i} className="flex items-start gap-2">
                    <TrendingUp className="w-4 h-4 text-green-400 mt-0.5 flex-shrink-0" />
                    <span className="text-xs sm:text-sm text-slate-200 break-words">{metric}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
          
          {/* Competitive Positioning */}
          {strategy.competitive_positioning && (
            <div className="p-3 sm:p-4 bg-orange-600/10 border border-orange-600/30 rounded-lg">
              <div className="text-xs text-orange-400 mb-2 font-semibold">Competitive Positioning</div>
              <div className="text-xs sm:text-sm text-slate-200 break-words leading-relaxed">{strategy.competitive_positioning}</div>
            </div>
          )}
          
          {/* Scaling Potential */}
          {strategy.scaling_potential && (
            <div className="mt-4 p-3 bg-slate-700/30 rounded-lg">
              <div className="text-xs text-slate-400 mb-1">Scaling Potential</div>
              <div className="text-xs sm:text-sm text-white break-words">{strategy.scaling_potential}</div>
            </div>
          )}
        </div>
      ))}
    </div>
  )}
</div>

  {/* Competitive Intelligence */}
<div className="bg-slate-800/30 backdrop-blur border border-slate-700 rounded-xl sm:rounded-2xl overflow-hidden">
  <button
    onClick={() => toggleSection('competitive')}
    className="w-full p-4 sm:p-6 flex items-center justify-between hover:bg-slate-800/50 transition-all"
  >
    <div className="flex items-center gap-2 sm:gap-3">
      <Eye className="w-5 h-5 sm:w-6 sm:h-6 text-red-500 flex-shrink-0" />
      <div className="text-left">
        <h3 className="text-base sm:text-lg md:text-xl font-bold text-white">Competitive Intelligence</h3>
        <p className="text-xs sm:text-sm text-slate-400 mt-1">
          {results?.competitive_intelligence?.direct_competitors?.length || 3} direct competitors identified
        </p>
      </div>
    </div>
    {expandedSections.competitive ? <ChevronUp className="w-4 h-4 sm:w-5 sm:h-5 text-slate-400 flex-shrink-0" /> : <ChevronDown className="w-4 h-4 sm:w-5 sm:h-5 text-slate-400 flex-shrink-0" />}
  </button>
  
  {expandedSections.competitive && (
    <div className="px-4 sm:px-6 pb-4 sm:pb-6">
      <div className="space-y-3 sm:space-y-4 mb-4 sm:mb-6">
        {results?.competitive_intelligence?.direct_competitors?.map((competitor, i) => (
          <div key={i} className="p-4 sm:p-5 bg-red-600/10 border border-red-600/30 rounded-lg sm:rounded-xl">
            <div className="flex flex-col sm:flex-row items-start justify-between gap-3 mb-3">
              <div className="flex-1 min-w-0">
                <span className="font-semibold text-white text-base sm:text-lg break-words">{competitor.name}</span>
                {competitor.market_share && (
                  <span className="ml-0 sm:ml-3 block sm:inline text-xs sm:text-sm text-red-300 mt-1 sm:mt-0">
                    {competitor.market_share} market share
                  </span>
                )}
              </div>
              <span className={`px-3 py-1 text-xs font-semibold rounded-full whitespace-nowrap ${
                competitor.threat_level === 'High' ? 'bg-red-600/20 text-red-300' :
                competitor.threat_level === 'Medium' ? 'bg-orange-600/20 text-orange-300' :
                'bg-yellow-600/20 text-yellow-300'
              }`}>
                {competitor.threat_level} Threat
              </span>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 sm:gap-4 text-xs sm:text-sm mb-3">
              <div>
                <span className="text-green-300 font-semibold">Strengths: </span>
                <span className="text-slate-200 break-words">
                  {Array.isArray(competitor.strengths) ? competitor.strengths.join(', ') : competitor.strengths}
                </span>
              </div>
              <div>
                <span className="text-red-300 font-semibold">Weaknesses: </span>
                <span className="text-slate-200 break-words">
                  {Array.isArray(competitor.weaknesses) ? competitor.weaknesses.join(', ') : competitor.weaknesses}
                </span>
              </div>
            </div>
            
            {competitor.funding_status && (
              <div className="text-xs sm:text-sm text-slate-300 bg-slate-700/30 rounded-lg p-3 mb-2 break-words">
                <span className="text-slate-400">Funding: </span>
                {competitor.funding_status}
              </div>
            )}
            
            {competitor.recent_developments && (
              <div className="text-xs sm:text-sm text-slate-300 bg-slate-700/30 rounded-lg p-3 mb-2 break-words">
                <span className="text-slate-400">Recent: </span>
                {competitor.recent_developments}
              </div>
            )}
            
            {competitor.competitive_response && (
              <div className="text-xs sm:text-sm text-blue-300 bg-blue-600/10 rounded-lg p-3 break-words">
                <span className="text-blue-400 font-semibold">How to compete: </span>
                {competitor.competitive_response}
              </div>
            )}
          </div>
        ))}
      </div>
      
      {results?.competitive_intelligence?.market_gaps && (
        <div className="bg-slate-700/30 rounded-lg sm:rounded-xl p-4 sm:p-5 mb-4">
          <h4 className="font-semibold text-sm sm:text-base text-white mb-3 sm:mb-4">Market Gaps & Opportunities</h4>
          <div className="space-y-2">
            {results.competitive_intelligence.market_gaps.map((gap, i) => (
              <div key={i} className="flex items-start gap-2">
                <Target className="w-3 h-3 sm:w-4 sm:h-4 text-green-500 mt-0.5 flex-shrink-0" />
                <span className="text-xs sm:text-sm text-slate-200 break-words">{gap}</span>
              </div>
            ))}
          </div>
        </div>
      )}
      
     {results?.competitive_intelligence?.barrier_to_entry_analysis && (
  <div className="bg-slate-700/30 rounded-lg sm:rounded-xl p-4 sm:p-5">
    {/* Header with Overall Height prominently displayed */}
    <div className="flex items-center justify-between mb-4">
      <h4 className="font-semibold text-sm sm:text-base text-white">Barrier to Entry Analysis</h4>
      <span className={`px-3 py-1.5 rounded-full text-sm font-bold ${
        results.competitive_intelligence.barrier_to_entry_analysis.overall_barrier_height === 'High' ? 'bg-red-600/20 text-red-300 border border-red-600/30' :
        results.competitive_intelligence.barrier_to_entry_analysis.overall_barrier_height === 'Medium' ? 'bg-yellow-600/20 text-yellow-300 border border-yellow-600/30' :
        'bg-green-600/20 text-green-300 border border-green-600/30'
      }`}>
        {results.competitive_intelligence.barrier_to_entry_analysis.overall_barrier_height || 'Medium'} Overall
      </span>
    </div>
    
    {/* Capital Breakdown - Detailed */}
    <div className="mb-4">
      <div className="text-xs text-slate-400 mb-2">Capital Requirements Breakdown</div>
      <div className="bg-slate-800/50 rounded-lg p-3 mb-2">
        <div className="text-sm font-semibold text-white mb-2">
          Minimum: {results.competitive_intelligence.barrier_to_entry_analysis.capital_requirements}
        </div>
        <div className="text-xs text-slate-300 leading-relaxed">
          {results.competitive_intelligence.barrier_to_entry_analysis.capital_breakdown}
        </div>
      </div>
    </div>
    
    {/* Time to Market */}
    <div className="mb-4">
      <div className="text-xs text-slate-400 mb-2">Time to Market</div>
      <div className="bg-slate-800/50 rounded-lg p-3">
        <div className="text-lg font-bold text-cyan-400">
          {results.competitive_intelligence.barrier_to_entry_analysis.time_to_market}
        </div>
        <div className="text-xs text-slate-300 mt-1">
          Realistic timeline to launch with {analysisDetails.budget || 'your budget'} and {analysisDetails.goal || 'your goals'}
        </div>
      </div>
    </div>
    
    {/* Barriers Grid */}
    <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-4">
      <div className="bg-red-600/10 border border-red-600/30 rounded-lg p-3">
        <div className="text-xs text-red-400 mb-1 font-semibold">Regulatory</div>
        <div className="text-xs text-slate-200">
          {results.competitive_intelligence.barrier_to_entry_analysis.regulatory_barriers}
        </div>
      </div>
      <div className="bg-orange-600/10 border border-orange-600/30 rounded-lg p-3">
        <div className="text-xs text-orange-400 mb-1 font-semibold">Technical</div>
        <div className="text-xs text-slate-200">
          {results.competitive_intelligence.barrier_to_entry_analysis.technical_barriers}
        </div>
      </div>
      <div className="bg-yellow-600/10 border border-yellow-600/30 rounded-lg p-3">
        <div className="text-xs text-yellow-400 mb-1 font-semibold">Market Access</div>
        <div className="text-xs text-slate-200">
          {results.competitive_intelligence.barrier_to_entry_analysis.market_access_barriers}
        </div>
      </div>
    </div>
    
    {/* Strategic Advantages - Dynamic to user's situation */}
    <div className="bg-blue-600/10 border border-blue-600/30 rounded-lg p-4">
      <div className="text-sm font-semibold text-blue-300 mb-2 flex items-center gap-2">
        <Shield className="w-4 h-4" />
        Your Strategic Advantages with {analysisDetails.budget || 'This Budget'}
      </div>
      <div className="space-y-2">
        {results.competitive_intelligence.barrier_to_entry_analysis.strategic_advantages?.map((advantage, i) => (
          <div key={i} className="flex items-start gap-2">
            <CheckCircle className="w-4 h-4 text-green-400 mt-0.5 flex-shrink-0" />
            <span className="text-xs sm:text-sm text-slate-200">{advantage}</span>
          </div>
        ))}
      </div>
    </div>
  </div>
)}
    </div>
  )}
</div>

 {/* Financial Projections */}
<div className="bg-slate-800/30 backdrop-blur border border-slate-700 rounded-xl sm:rounded-2xl overflow-hidden">
  <button
    onClick={() => toggleSection('financial')}
    className="w-full p-4 sm:p-6 flex items-center justify-between hover:bg-slate-800/50 transition-all"
  >
    <div className="flex items-center gap-2 sm:gap-3">
      <TrendingUp className="w-5 h-5 sm:w-6 sm:h-6 text-green-500 flex-shrink-0" />
      <div className="text-left">
        <h3 className="text-base sm:text-lg md:text-xl font-bold text-white">Financial Projections</h3>
        <p className="text-xs sm:text-sm text-slate-400 mt-1">3-year revenue forecast and metrics</p>
      </div>
    </div>
    {expandedSections.financial ? <ChevronUp className="w-4 h-4 sm:w-5 sm:h-5 text-slate-400 flex-shrink-0" /> : <ChevronDown className="w-4 h-4 sm:w-5 sm:h-5 text-slate-400 flex-shrink-0" />}
  </button>
  
  {expandedSections.financial && (
    <div className="px-4 sm:px-6 pb-4 sm:pb-6">
      {/* Year Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3 sm:gap-4 mb-6">
        <div className="bg-gradient-to-r from-green-600/10 to-emerald-600/10 border border-green-600/30 rounded-lg sm:rounded-xl p-4 sm:p-6">
          <div className="text-xs sm:text-sm text-slate-400 mb-2">Year 1</div>
          <div className="text-2xl sm:text-3xl font-bold text-green-400 mb-2 break-words">
            {results?.financial_projections?.year_1?.revenue}
          </div>
          <div className="text-xs sm:text-sm text-slate-300 mb-1">
            {results?.financial_projections?.year_1?.customers} customers
          </div>
          <div className="text-xs text-slate-400 mb-3">
            Margin: {results?.financial_projections?.year_1?.gross_margin}
          </div>
          {results?.financial_projections?.year_1?.revenue_breakdown && (
            <div className="text-xs text-slate-400 mt-2 pt-2 border-t border-green-600/20">
              {results.financial_projections.year_1.revenue_breakdown}
            </div>
          )}
          {results?.financial_projections?.year_1?.milestone && (
            <div className="mt-3 p-2 bg-green-600/10 rounded text-xs text-green-300">
              🎯 {results.financial_projections.year_1.milestone}
            </div>
          )}
        </div>
        
        <div className="bg-gradient-to-r from-blue-600/10 to-cyan-600/10 border border-blue-600/30 rounded-lg sm:rounded-xl p-4 sm:p-6">
          <div className="text-xs sm:text-sm text-slate-400 mb-2">Year 2</div>
          <div className="text-2xl sm:text-3xl font-bold text-blue-400 mb-2 break-words">
            {results?.financial_projections?.year_2?.revenue}
          </div>
          <div className="text-xs sm:text-sm text-slate-300 mb-1">
            {results?.financial_projections?.year_2?.customers} customers
          </div>
          <div className="text-xs text-slate-400 mb-3">
            Margin: {results?.financial_projections?.year_2?.gross_margin}
          </div>
          {results?.financial_projections?.year_2?.revenue_breakdown && (
            <div className="text-xs text-slate-400 mt-2 pt-2 border-t border-blue-600/20">
              {results.financial_projections.year_2.revenue_breakdown}
            </div>
          )}
          {results?.financial_projections?.year_2?.milestone && (
            <div className="mt-3 p-2 bg-blue-600/10 rounded text-xs text-blue-300">
              🎯 {results.financial_projections.year_2.milestone}
            </div>
          )}
        </div>
        
        <div className="bg-gradient-to-r from-purple-600/10 to-pink-600/10 border border-purple-600/30 rounded-lg sm:rounded-xl p-4 sm:p-6">
          <div className="text-xs sm:text-sm text-slate-400 mb-2">Year 3</div>
          <div className="text-2xl sm:text-3xl font-bold text-purple-400 mb-2 break-words">
            {results?.financial_projections?.year_3?.revenue}
          </div>
          <div className="text-xs sm:text-sm text-slate-300 mb-1">
            {results?.financial_projections?.year_3?.customers} customers
          </div>
          <div className="text-xs text-slate-400 mb-3">
            Margin: {results?.financial_projections?.year_3?.gross_margin}
          </div>
          {results?.financial_projections?.year_3?.revenue_breakdown && (
            <div className="text-xs text-slate-400 mt-2 pt-2 border-t border-purple-600/20">
              {results.financial_projections.year_3.revenue_breakdown}
            </div>
          )}
          {results?.financial_projections?.year_3?.milestone && (
            <div className="mt-3 p-2 bg-purple-600/10 rounded text-xs text-purple-300">
              🎯 {results.financial_projections.year_3.milestone}
            </div>
          )}
        </div>
      </div>
      
      {/* How to Achieve These Numbers */}
      <div className="mb-6 space-y-4">
        <h4 className="font-semibold text-base sm:text-lg text-white flex items-center gap-2">
          <Lightbulb className="w-5 h-5 text-yellow-400" />
          How to Achieve These Numbers
        </h4>
        
        {results?.financial_projections?.year_1?.customer_acquisition_strategy && (
          <div className="bg-green-600/10 border border-green-600/30 rounded-lg sm:rounded-xl p-4">
            <div className="flex items-center gap-2 mb-2">
              <div className="text-sm font-semibold text-green-400">Year 1 Strategy</div>
              {results?.financial_projections?.year_1?.key_assumptions && (
                <span className="text-xs text-slate-400">
                  ({results.financial_projections.year_1.key_assumptions})
                </span>
              )}
            </div>
            <p className="text-xs sm:text-sm text-slate-200 break-words leading-relaxed">
              {results.financial_projections.year_1.customer_acquisition_strategy}
            </p>
          </div>
        )}
        
        {results?.financial_projections?.year_2?.customer_acquisition_strategy && (
          <div className="bg-blue-600/10 border border-blue-600/30 rounded-lg sm:rounded-xl p-4">
            <div className="flex items-center gap-2 mb-2">
              <div className="text-sm font-semibold text-blue-400">Year 2 Strategy</div>
              {results?.financial_projections?.year_2?.key_assumptions && (
                <span className="text-xs text-slate-400">
                  ({results.financial_projections.year_2.key_assumptions})
                </span>
              )}
            </div>
            <p className="text-xs sm:text-sm text-slate-200 break-words leading-relaxed">
              {results.financial_projections.year_2.customer_acquisition_strategy}
            </p>
          </div>
        )}
        
        {results?.financial_projections?.year_3?.customer_acquisition_strategy && (
          <div className="bg-purple-600/10 border border-purple-600/30 rounded-lg sm:rounded-xl p-4">
            <div className="flex items-center gap-2 mb-2">
              <div className="text-sm font-semibold text-purple-400">Year 3 Strategy</div>
              {results?.financial_projections?.year_3?.key_assumptions && (
                <span className="text-xs text-slate-400">
                  ({results.financial_projections.year_3.key_assumptions})
                </span>
              )}
            </div>
            <p className="text-xs sm:text-sm text-slate-200 break-words leading-relaxed">
              {results.financial_projections.year_3.customer_acquisition_strategy}
            </p>
          </div>
        )}
      </div>
      
      {/* Detailed Break-even Analysis */}
      {results?.financial_projections?.break_even_analysis && (
        <div className="bg-yellow-600/10 border border-yellow-600/30 rounded-lg sm:rounded-xl p-4 sm:p-5 mb-6">
          <h4 className="font-semibold text-sm sm:text-base text-yellow-300 mb-4 flex items-center gap-2">
            <TrendingUp className="w-5 h-5" />
            Detailed Break-even Analysis
          </h4>
          
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 sm:gap-4 mb-4">
            <div className="bg-slate-800/50 rounded-lg p-3">
              <div className="text-xs text-slate-400 mb-1">Break-even Month</div>
              <div className="text-lg sm:text-xl font-bold text-white">
                Month {results.financial_projections.break_even_analysis.break_even_month}
              </div>
            </div>
            <div className="bg-slate-800/50 rounded-lg p-3">
              <div className="text-xs text-slate-400 mb-1">Customers Needed</div>
              <div className="text-lg sm:text-xl font-bold text-white">
                {results.financial_projections.break_even_analysis.break_even_customers}
              </div>
            </div>
            <div className="bg-slate-800/50 rounded-lg p-3">
              <div className="text-xs text-slate-400 mb-1">Monthly Revenue</div>
              <div className="text-lg sm:text-xl font-bold text-white break-words">
                {results.financial_projections.break_even_analysis.break_even_revenue}
              </div>
            </div>
          </div>
          
          {results.financial_projections.break_even_analysis.path_to_breakeven && (
            <div className="mb-4 p-3 sm:p-4 bg-slate-800/50 rounded-lg">
              <div className="text-xs text-slate-400 mb-2 font-semibold">Path to Break-even</div>
              <div className="text-xs sm:text-sm text-slate-200 break-words leading-relaxed">
                {results.financial_projections.break_even_analysis.path_to_breakeven}
              </div>
            </div>
          )}
          
          {results.financial_projections.break_even_analysis.sensitivity_analysis && (
            <div className="mb-4 p-3 sm:p-4 bg-orange-600/10 border border-orange-600/30 rounded-lg">
              <div className="text-xs text-orange-400 mb-2 font-semibold">Sensitivity Analysis</div>
              <div className="text-xs sm:text-sm text-slate-200 break-words leading-relaxed">
                {results.financial_projections.break_even_analysis.sensitivity_analysis}
              </div>
            </div>
          )}
          
          {results.financial_projections.break_even_analysis.funding_requirement && (
            <div className="p-3 sm:p-4 bg-blue-600/10 border border-blue-600/30 rounded-lg">
              <div className="text-xs text-blue-400 mb-2 font-semibold flex items-center gap-2">
                <DollarSign className="w-4 h-4" />
                Funding Requirement
              </div>
              <div className="text-xs sm:text-sm text-slate-200 break-words leading-relaxed">
                {results.financial_projections.break_even_analysis.funding_requirement}
              </div>
            </div>
          )}
        </div>
      )}
      
      {/* Exit Scenarios */}
      {results?.financial_projections?.exit_scenarios && results.financial_projections.exit_scenarios.length > 0 && (
        <div className="space-y-3">
          <h4 className="font-semibold text-sm sm:text-base text-white flex items-center gap-2">
            <Rocket className="w-5 h-5 text-purple-400" />
            Exit Scenarios
          </h4>
          {results.financial_projections.exit_scenarios.map((scenario, idx) => (
            <div key={idx} className="bg-slate-700/30 border border-slate-600/30 rounded-lg sm:rounded-xl p-4 sm:p-5">
              <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 mb-3">
                <div className="flex-1">
                  <div className="font-semibold text-sm sm:text-base text-white mb-1">{scenario.type}</div>
                  <div className="text-xs sm:text-sm text-slate-300">
                    Timeline: {scenario.timeline} • Valuation: <span className="text-green-400 font-semibold">{scenario.valuation_range}</span>
                  </div>
                </div>
                <span className={`px-3 py-1.5 text-xs font-semibold rounded-full whitespace-nowrap ${
                  scenario.likelihood === 'High' ? 'bg-green-600/20 text-green-300' :
                  scenario.likelihood === 'Medium' ? 'bg-yellow-600/20 text-yellow-300' :
                  'bg-slate-600/20 text-slate-300'
                }`}>
                  {scenario.likelihood} Likelihood
                </span>
              </div>
              
              {scenario.valuation_basis && (
                <div className="mb-3 p-3 bg-slate-800/50 rounded-lg">
                  <div className="text-xs text-slate-400 mb-1">Valuation Basis</div>
                  <div className="text-xs sm:text-sm text-slate-200 break-words">{scenario.valuation_basis}</div>
                </div>
              )}
              
              {scenario.potential_acquirers && scenario.potential_acquirers.length > 0 && (
                <div className="mb-3">
                  <div className="text-xs text-slate-400 mb-2">Potential Acquirers</div>
                  <div className="flex flex-wrap gap-2">
                    {scenario.potential_acquirers.map((acquirer, i) => (
                      <span key={i} className="px-3 py-1.5 bg-purple-600/20 text-purple-300 text-xs sm:text-sm rounded-lg break-words">
                        {acquirer}
                      </span>
                    ))}
                  </div>
                </div>
              )}
              
              {scenario.trigger_events && (
                <div className="p-3 bg-blue-600/10 border border-blue-600/30 rounded-lg">
                  <div className="text-xs text-blue-400 mb-1 font-semibold">Trigger Events</div>
                  <div className="text-xs sm:text-sm text-slate-200 break-words">{scenario.trigger_events}</div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  )}
</div>

 {/* Risk Assessment - Shows ALL risks */}
<div className="bg-slate-800/30 backdrop-blur border border-slate-700 rounded-xl sm:rounded-2xl overflow-hidden">
  <button
    onClick={() => toggleSection('risks')}
    className="w-full p-4 sm:p-6 flex items-center justify-between hover:bg-slate-800/50 transition-all"
  >
    <div className="flex items-center gap-2 sm:gap-3">
      <AlertTriangle className="w-5 h-5 sm:w-6 sm:h-6 text-yellow-500 flex-shrink-0" />
      <div className="text-left">
        <h3 className="text-base sm:text-lg md:text-xl font-bold text-white">Risk Assessment</h3>
        <p className="text-xs sm:text-sm text-slate-400 mt-1">
          {results?.risk_assessment?.length || 5} key risks identified across multiple categories
        </p>
      </div>
    </div>
    {expandedSections.risks ? <ChevronUp className="w-4 h-4 sm:w-5 sm:h-5 text-slate-400 flex-shrink-0" /> : <ChevronDown className="w-4 h-4 sm:w-5 sm:h-5 text-slate-400 flex-shrink-0" />}
  </button>
  
  {expandedSections.risks && (
    <div className="px-4 sm:px-6 pb-4 sm:pb-6 space-y-3 sm:space-y-4">
      {(results?.risk_assessment || []).map((risk, idx) => (
        <div key={idx} className="bg-yellow-600/10 border border-yellow-600/30 rounded-lg sm:rounded-xl p-4 sm:p-5">
          <div className="flex flex-col sm:flex-row items-start justify-between gap-3 mb-3">
            <div className="flex-1">
              <h4 className="font-semibold text-sm sm:text-base text-white break-words mb-1">{risk.risk}</h4>
              <span className="text-xs text-slate-400">{risk.category}</span>
            </div>
            <div className="flex gap-2">
              <span className={`px-2 py-1 text-xs rounded-full whitespace-nowrap ${
                risk.probability === 'High' ? 'bg-red-600/20 text-red-300' :
                risk.probability === 'Medium' ? 'bg-orange-600/20 text-orange-300' :
                'bg-green-600/20 text-green-300'
              }`}>
                {risk.probability} Prob
              </span>
              <span className={`px-2 py-1 text-xs rounded-full whitespace-nowrap ${
                risk.impact === 'High' ? 'bg-red-600/20 text-red-300' :
                risk.impact === 'Medium' ? 'bg-orange-600/20 text-orange-300' :
                'bg-green-600/20 text-green-300'
              }`}>
                {risk.impact} Impact
              </span>
            </div>
          </div>
          
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4 text-xs sm:text-sm mb-3">
            <div>
              <span className="text-slate-400">Financial Impact: </span>
              <span className="text-white font-medium break-words">{risk.financial_impact}</span>
            </div>
            <div>
              <span className="text-slate-400">Timeline: </span>
              <span className="text-white font-medium">{risk.timeline}</span>
            </div>
          </div>
          
          <div className="space-y-2">
            <div className="bg-slate-700/30 rounded-lg p-3">
              <div className="text-xs text-slate-400 mb-1">Mitigation Strategy</div>
              <div className="text-xs sm:text-sm text-slate-200 break-words">{risk.mitigation_strategy}</div>
            </div>
            <div className="bg-slate-700/30 rounded-lg p-3">
              <div className="text-xs text-slate-400 mb-1">Contingency Plan</div>
              <div className="text-xs sm:text-sm text-slate-200 break-words">{risk.contingency_plan}</div>
            </div>
            {risk.early_warning_signs && (
              <div className="bg-red-600/10 border border-red-600/30 rounded-lg p-3">
                <div className="text-xs text-red-400 mb-2 font-semibold">Early Warning Signs</div>
                <div className="space-y-1">
                  {risk.early_warning_signs.map((sign, i) => (
                    <div key={i} className="flex items-start gap-2">
                      <AlertTriangle className="w-3 h-3 text-red-400 mt-0.5 flex-shrink-0" />
                      <span className="text-xs text-slate-200 break-words">{sign}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      ))}
    </div>
  )}
</div>

  {/* Implementation Roadmap */}
  <div className="bg-slate-800/30 backdrop-blur border border-slate-700 rounded-xl sm:rounded-2xl overflow-hidden">
    <button
      onClick={() => toggleSection('roadmap')}
      className="w-full p-4 sm:p-6 flex items-center justify-between hover:bg-slate-800/50 transition-all"
    >
      <div className="flex items-center gap-2 sm:gap-3">
        <Map className="w-5 h-5 sm:w-6 sm:h-6 text-indigo-500 flex-shrink-0" />
        <div className="text-left">
          <h3 className="text-base sm:text-lg md:text-xl font-bold text-white">Implementation Roadmap</h3>
          <p className="text-xs sm:text-sm text-slate-400 mt-1">3-phase strategic execution plan</p>
        </div>
      </div>
      {expandedSections.roadmap ? <ChevronUp className="w-4 h-4 sm:w-5 sm:h-5 text-slate-400 flex-shrink-0" /> : <ChevronDown className="w-4 h-4 sm:w-5 sm:h-5 text-slate-400 flex-shrink-0" />}
    </button>
    
    {expandedSections.roadmap && (
      <div className="px-4 sm:px-6 pb-4 sm:pb-6">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 sm:gap-6">
          {Object.entries(results?.implementation_roadmap || {}).slice(0, 3).map(([key, phase], index) => (
            <div key={key} className="bg-indigo-600/10 border border-indigo-600/30 rounded-lg sm:rounded-xl p-4 sm:p-6">
              <div className="flex items-center gap-2 sm:gap-3 mb-4">
                <div className="w-8 h-8 sm:w-10 sm:h-10 bg-indigo-600 text-white rounded-full flex items-center justify-center font-bold text-sm sm:text-base flex-shrink-0">
                  {index + 1}
                </div>
                <div className="flex-1 min-w-0">
                  <h4 className="font-semibold text-sm sm:text-base text-white break-words">{phase.name}</h4>
                  <div className="text-xs text-slate-400">{phase.duration}</div>
                </div>
              </div>
              
              <div className="text-lg sm:text-xl font-bold text-indigo-300 mb-4 break-words">
                {phase.budget}
              </div>
              
              <div className="mb-4">
                <div className="text-xs text-slate-400 mb-2">Key Activities</div>
                <div className="space-y-1">
                  {phase.key_activities?.slice(0, 3).map((activity, i) => (
                    <div key={i} className="text-xs text-slate-200 bg-slate-700/30 rounded px-2 py-1 break-words">
                      {activity}
                    </div>
                  ))}
                </div>
              </div>
              
              <div className="mb-4">
                <div className="text-xs text-slate-400 mb-2">Success Metrics</div>
                <div className="space-y-1">
                  {phase.success_metrics?.slice(0, 2).map((metric, i) => (
                    <div key={i} className="flex items-start gap-1">
                      <div className="w-1 h-1 bg-green-500 rounded-full mt-1.5 flex-shrink-0"></div>
                      <span className="text-xs text-slate-200 break-words">{metric}</span>
                    </div>
                  ))}
                </div>
              </div>
              
              {phase.team_required && (
                <div>
                  <div className="text-xs text-slate-400 mb-2">Team Required</div>
                  <div className="flex flex-wrap gap-1">
                    {phase.team_required.slice(0, 3).map((role, i) => (
                      <span key={i} className="px-2 py-1 bg-slate-700/30 text-xs text-slate-300 rounded break-words">
                        {role}
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    )}
  </div>
</div>

{/* Strategic Recommendations */}
<div className="mt-6 sm:mt-8 bg-orange-600/10 border border-orange-600/30 rounded-xl sm:rounded-2xl p-4 sm:p-6">
  <div className="flex items-center gap-2 sm:gap-3 mb-4 sm:mb-6">
    <Compass className="w-5 h-5 sm:w-6 sm:h-6 text-orange-500" />
    <h3 className="text-lg sm:text-xl md:text-2xl font-bold text-white">Strategic Recommendations</h3>
  </div>
  
  <div className="grid grid-cols-1 md:grid-cols-2 gap-3 sm:gap-4">
    {(results?.strategic_recommendations || []).slice(0, 8).map((rec, index) => (
      <div key={index} className="flex items-start gap-2 sm:gap-3 p-3 sm:p-4 bg-orange-600/10 border border-orange-600/30 rounded-lg sm:rounded-xl">
        <div className="w-5 h-5 sm:w-6 sm:h-6 bg-orange-500 text-white rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0 mt-0.5">
          {index + 1}
        </div>
        <span className="text-slate-200 text-xs sm:text-sm leading-relaxed break-words">{rec}</span>
      </div>
    ))}
  </div>
</div>
{/* ALL EXPANDABLE SECTIONS END HERE */}
          

{/* Follow-up Questions Section */}
<div className="mt-6 sm:mt-8 bg-slate-800/30 backdrop-blur border border-slate-700 rounded-xl sm:rounded-2xl p-4 sm:p-6">
  <div className="flex items-center gap-2 sm:gap-3 mb-3 sm:mb-4">
    <MessageSquare className="w-5 h-5 sm:w-6 sm:h-6 text-blue-500" />
    <h3 className="text-lg sm:text-xl md:text-2xl font-bold text-white">Ask Follow-up Questions</h3>
  </div>
  
  <p className="text-xs sm:text-sm text-slate-400 mb-3 sm:mb-4">
    Get deeper insights about specific aspects of this market analysis
  </p>
  
  <div className="flex flex-col sm:flex-row gap-3 mb-4">
    <input
      type="text"
      value={followUpQuestion}
      onChange={(e) => setFollowUpQuestion(e.target.value)}
      onKeyPress={(e) => e.key === 'Enter' && askFollowUp()}
      placeholder="Ask about regulations, competitors, market trends..."
      className="flex-1 px-3 sm:px-4 py-2 sm:py-3 bg-slate-800/50 border border-slate-700 rounded-lg sm:rounded-xl text-sm sm:text-base text-white placeholder-slate-400 focus:border-blue-600 focus:outline-none transition-all"
    />
    <button
      onClick={askFollowUp}
      disabled={followUpLoading || !followUpQuestion.trim()}
      className="w-full sm:w-auto px-4 sm:px-6 py-2 sm:py-3 bg-gradient-to-r from-blue-600 to-cyan-600 text-white text-sm sm:text-base font-semibold rounded-lg sm:rounded-xl hover:from-blue-700 hover:to-cyan-700 disabled:from-slate-600 disabled:to-slate-700 disabled:cursor-not-allowed transition-all"
    >
      {followUpLoading ? 'Analyzing...' : 'Ask'}
    </button>
  </div>
  
  <div className="mb-4">
    <p className="text-xs text-slate-500 mb-2">Suggested questions:</p>
    <div className="flex gap-2 overflow-x-auto pb-2">
      {[
        'What are the regulatory requirements?',
        'Who are the main competitors?',
        'What are typical profit margins?',
        'Best marketing channels?'
      ].map((suggestion, i) => (
        <button
          key={i}
          onClick={() => setFollowUpQuestion(suggestion)}
          className="flex-shrink-0 px-3 py-1.5 bg-blue-600/10 border border-blue-600/30 rounded-lg text-blue-400 text-xs hover:bg-blue-600/20 transition-all whitespace-nowrap"
        >
          {suggestion}
        </button>
      ))}
    </div>
  </div>
  
  {followUpResponses.length > 0 && (
    <div className="mt-4 sm:mt-6 space-y-3 sm:space-y-4">
      {followUpResponses.map((response, idx) => (
        <div key={idx} className="bg-slate-800/50 rounded-lg sm:rounded-xl p-3 sm:p-4">
          <div className="font-semibold text-blue-400 text-sm sm:text-base mb-2 break-words">
            Q: {response.question}
          </div>
          <div className="text-slate-200 text-xs sm:text-sm break-words leading-relaxed">
            {response.answer.split('\n').map((line, lineIdx) => {
              if (!line.trim()) return <br key={lineIdx} />;
              
              // Split by **text** pattern
              const parts = line.split(/(\*\*[^*]+\*\*)/g);
              const formatted = parts.map((part, i) => {
                if (part.startsWith('**') && part.endsWith('**')) {
                  return <strong key={i} className="font-semibold text-white">{part.slice(2, -2)}</strong>;
                }
                return <span key={i}>{part}</span>;
              });
              
              // Handle bullet points
              if (line.trim().startsWith('*') || line.trim().startsWith('-')) {
                return (
                  <div key={lineIdx} className="flex gap-2 mb-1.5">
                    <span className="text-cyan-400 flex-shrink-0">•</span>
                    <span className="flex-1">{formatted}</span>
                  </div>
                );
              }
              
              // Regular paragraph
              return <div key={lineIdx} className="mb-2">{formatted}</div>;
            })}
          </div>
          <div className="text-xs text-slate-500 mt-2">
            {new Date(response.timestamp).toLocaleString()}
          </div>
        </div>
      ))}
    </div>
  )}
</div>

          {/* Footer */}
          <div className="text-center mt-8 sm:mt-10 py-4 sm:py-5 border-t border-slate-700">
            <p className="text-slate-400 text-xs sm:text-sm break-words px-4">
              Analysis ID: {results?.analysis_id || `gmi_${Date.now()}`} • 
              Generated on {new Date().toLocaleDateString()} • 
              Confidence Score: {results?.confidence_score || 92}%
            </p>
              <p className="text-xs text-slate-500 mt-2 break-words px-4">
          Powered by WhyDidItDie • Real-time Market Intelligence
        </p>
          </div>
        </div>
      {showSaveSuccess && (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm">
        <div className="bg-gradient-to-br from-green-600 to-emerald-600 rounded-2xl p-8 shadow-2xl max-w-md mx-4">
          <div className="flex flex-col items-center text-center">
            <div className="w-16 h-16 bg-white/20 rounded-full flex items-center justify-center mb-4">
              <Check className="w-10 h-10 text-white" strokeWidth={3} />
            </div>
            <h3 className="text-2xl font-bold text-white mb-2">Analysis Saved!</h3>
            <p className="text-green-100">
              Your market intelligence report has been successfully saved to your dashboard.
            </p>
          </div>
        </div>
      </div>
    )}
      </div>

    );
  }

  return null;
};

export default GlobalMarketIntelligence;